import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../auth.js';
import { FiTrash2, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import { useFlexibleTranslation } from '../contexts/LanguageContext';
import { FiGlobe, FiFileText, FiMessageSquare } from 'react-icons/fi';
import { FiPaperclip } from 'react-icons/fi';
import PdfViewerModal from './PdfViewerModal';
import ResearchProgressIndicator from './ResearchProgressIndicator.jsx';
import { resolveResearchProgressMessage } from '../utils/researchProgress.js';
import CompanyService from '../services/companyService.js';
import { phResearchQuerySent, phResearchThumb, phTourResearchUsed, phChatbotResponseFailed, phDocumentSelected, phChatbotResponse, phChatbotFindingGenerated, phCitationClicked } from '../utils/posthogEvents';

const GPT_API_BASE = process.env.REACT_APP_GPT_API_BASE;
const TEMP_UPLOAD_API_BASE = process.env.REACT_APP_TEMP_UPLOAD_API_BASE;

const PENDING_REQ_KEY = (projectId, chatId) =>
  `codiii:pendingChatReq:${projectId}:${chatId}`;

function readPendingReqs(projectId, chatId) {
  try {
    return JSON.parse(localStorage.getItem(PENDING_REQ_KEY(projectId, chatId)) || "{}");
  } catch {
    return {};
  }
}

function writePendingReqs(projectId, chatId, data) {
  localStorage.setItem(PENDING_REQ_KEY(projectId, chatId), JSON.stringify(data));
}

function upsertPendingReq(projectId, chatId, botMsgId, payload) {
  const all = readPendingReqs(projectId, chatId);
  all[botMsgId] = { ...(all[botMsgId] || {}), ...payload, updatedAt: Date.now() };
  writePendingReqs(projectId, chatId, all);
}

function removePendingReq(projectId, chatId, botMsgId) {
  const all = readPendingReqs(projectId, chatId);
  delete all[botMsgId];
  writePendingReqs(projectId, chatId, all);
}


function getIdToken(auth) {
  return (
    auth?.user?.idToken ||
    auth?.idToken ||
    auth?.user?.id_token ||
    localStorage.getItem('id_token')
  );
}

function buildAuthHeaders(auth) {
  const h = { 'Content-Type': 'application/json' };
  const idTok = getIdToken(auth);
  if (idTok) {
    h.Authorization = `Bearer ${idTok}`;
  } else {
    console.warn('[auth] No id_token found. Headers will not include Authorization.');
  }
  return h;
}

async function gptListConversations(auth) {
  const r = await fetch(`${GPT_API_BASE}/chat/list`, { headers: buildAuthHeaders(auth) });
  if (!r.ok) throw new Error(await r.text());
  return r.json(); 
}

async function gptGetHistory(conversationId, auth) {
  const r = await fetch(`${GPT_API_BASE}/chat/history?conversationId=${encodeURIComponent(conversationId)}`, {
    headers: buildAuthHeaders(auth),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json(); 
}

async function gptSendMessage({ conversationId, userText }, auth) {
  const r = await fetch(`${GPT_API_BASE}/chat/send`, {
    method: 'POST',
    headers: buildAuthHeaders(auth),
    body: JSON.stringify({ conversationId: conversationId ?? null, userText }),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json(); 
}
async function gptDeleteConversation(conversationId, auth) {
  const headers = buildAuthHeaders(auth);       
 
  const r = await fetch(`${GPT_API_BASE}/chat/delete`, {
    method: "POST",
    headers,
    body: JSON.stringify({ conversationId }),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

const AttachButton = styled.button`
  position: relative;
  width: 40px;
  height: 40px;
  border: 1px solid #ffd7c3;
  background: ${({ $active }) => ($active ? '#ffefe7' : '#fff')};
  color: #e97024;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1000;

  &:hover {
    background: #ffe6db;
  }

  &::after {
    content: attr(data-title);
    position: absolute;
    bottom: 48px;
    left: 50%;
    transform: translateX(-50%) translateY(2px);
    background: #ffffff;
    color: #111827;
    font-size: 12px;
    line-height: 1;
    padding: 8px 10px;
    border-radius: 8px;
    white-space: nowrap;
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease, transform 0.15s ease;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #ffffff;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover::after,
  &:hover::before,
  &:focus-visible::after,
  &:focus-visible::before {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

const ChatbotContainer = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1 1 auto;     
  min-height: 0;      
  width: 100%;

  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
`;


const MarkdownWrapper = styled.div`
overflow-wrap: anywhere;
word-break: break-word;
  h1, h2, h3, h4, h5, h6 {
    margin: 1em 0 0.5em 0;
    color: #2D2D2D;
  }

  h2 {
    font-size: 1.2em;
  }

  p {
    margin: 0.5em 0;
  }
    
  li > p {
    display: inline;
    margin: 0;
  }

  ul, ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }

  li {
    margin: 0.25em 0;
  }
`;

const ChatContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0; 
  position: relative; 
  align-items: stretch; 
  overflow: hidden;
  height: 100%;
`;

const CenterWrap = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;


const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 16px;
  margin: 8px 0;
  background: ${({ $isSelected }) => $isSelected ? 'rgba(255, 107, 53, 0.05)' : '#fff'};
  border: 1px solid ${({ $isSelected }) => $isSelected ? '#e97024' : '#e5e5e5'};
  border-radius: 12px;
  transition: background 0.2s, border-color 0.2s;
  cursor: pointer;
  gap: 12px;
  box-shadow: none;

  &:hover {
    background: ${({ $isSelected }) => $isSelected ? 'rgba(255, 107, 53, 0.06)' : '#f9f9f9'};
  }

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    color: #3b82f6;
  }

  input[type="checkbox"] {
    appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid ${({ $isSelected }) => $isSelected ? '#e97024' : '#ccc'};
    border-radius: 4px;
    background: ${({ $isSelected }) => $isSelected ? '#e97024' : 'white'};
    display: inline-block;
    margin-right: 4px;
    position: relative;
    cursor: pointer;

    &:checked::after {
      content: '';
      position: absolute;
      left: 4px;
      top: 1px;
      width: 4px;
      height: 9px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
`;
const ChatWindow = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;

  width: clamp(560px, 52vw, 860px);
  max-width: 100%;
  margin: 0 auto;

  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
`;

const HeaderIconWrapper = styled.div`
  background: rgba(255, 107, 53, 0.1); 
  color: #e97024; 
  border-radius: 50%;
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 20px;
`;



const ChatBody = styled.div`
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  min-height: 0;
  position: relative;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ChatBodyInner = styled.div`
  width: 100%;
  margin: 0 auto; 
  min-width: 0;
`;



const ChatInputWrapper = styled.div`
  height: 80px;
  background: #fff;
  border-top: 1px solid #e5e5e5;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0 16px;      
  box-sizing: border-box;
`;

const ChatInputForm = styled.form`
  width: 100%;
  max-width: clamp(520px, 100%, 900px);  
  margin: 0 auto;
  display: flex;   
  min-width: 0;  
`;



const ChatInputBar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;         
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  padding: 6px 8px 6px 16px;
  gap: 8px;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;          
  border: none;
  background: #f7f7f8;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 16px;
  outline: none;
`;


const SendButton = styled.button`
  min-width: 60px;
  height: 40px;
  background: #e5e5e5;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  margin-left: 4px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;

  &:not(:disabled) {
    background: #e97024;
    color: #fff;
  }

  &:disabled {
    background: #e5e5e5;
    color: #fff;
    cursor: not-allowed;
  }
`;

const BotIconWrapper = styled.div`
  background: rgba(255, 107, 53, 0.1);
  color: #e97024;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
`;

const Message = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  margin: 10px 0;
`;


const MessageBubble = styled.div`
  background: ${({ $isUser, $thumb }) => {
    if ($isUser) return '#f0f0f0';
    if ($thumb === 'up') return 'rgba(22,163,74,0.08)';
    if ($thumb === 'down') return 'rgba(239,68,68,0.08)';
    return 'transparent';
  }};

  color: #2d2d2d;

  border: ${({ $isUser, $thumb }) => {
    if ($isUser) return 'none';
    if ($thumb === 'up') return '1px solid rgba(22,163,74,0.25)';
    if ($thumb === 'down') return '1px solid rgba(239,68,68,0.25)';
    return '1px solid transparent';
  }};

  border-radius: 12px;
  padding: 12px 14px;
  box-sizing: border-box;
  overflow-wrap: anywhere;
  word-break: break-word;

  flex: 0 0 auto;
  width: ${({ $pending }) => ($pending ? 'min(100%, 420px)' : 'fit-content')};
  max-width: 80%;
`;


const CustomCheckbox = styled.input`
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid #ccc;
  border-radius: 4px;
  background-color: ${({ checked }) => checked ? '#e97024' : '#fff'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0;
  flex-shrink: 0;
  position: relative;

  &:checked::after {
    content: '';
    position: absolute;
    width: 4px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
    <div style={{ width: '30px', height: '30px', border: '3px solid #f3f3f3', borderTop: '3px solid #e97024', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    <style>
      {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
  </div>
);

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const DocumentSelectionDialog = styled.div`
  background: white;
  border-radius: 8px;
  width: 80%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DialogHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin: 0;
    font-size: 1.2rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const SearchBar = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
`;


const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  background: #fff;
  color: #2d2d2d;

  &:focus {
    outline: none;
    border-color: #ccc;
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
  }

  &::placeholder {
    color: #aaa;
  }
`;


const FilterChips = styled.div`
  padding: 8px 16px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  border-bottom: 1px solid #eee;
`;

const FilterChip = styled.button`
  padding: 4px 12px;
  border-radius: 16px;
  border: 1px solid #ddd;
  background: ${props => props.$isActive ? '#007bff' : 'white'};
  color: ${props => props.$isActive ? 'white' : '#333'};
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    background: ${props => props.$isActive ? '#0056b3' : '#f5f5f5'};
  }
`;

const DocumentList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;


const DialogActions = styled.div`
  padding: 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const DialogButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;

  &.primary {
    background: #007bff;
    color: white;

    &:hover {
      background: #0056b3;
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: #f8f9fa;
    color: #333;
    border: 1px solid #ddd;

    &:hover {
      background: #e9ecef;
    }
  }
`;




const VoiceButton = styled.button`
  width: 40px;
  height: 40px;
  background: ${({ isRecording }) => (isRecording ? '#e97024' : '#343541')};
  color: #fff;
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;

  ${({ isRecording }) =>
    isRecording &&
    `
    animation: pulse 1s infinite;
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); }
    }
  `}
`;

const Sidebar = styled.div`
  width: clamp(260px, 28vw, 400px);
  background: #ffffff;
  z-index: 10;
  box-shadow: 4px 0 25px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.05);
  position: relative;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 1100px) {
    width: clamp(220px, 30vw, 340px);
    flex: 0 1 340px;
  }
  @media (max-width: 900px) {
    width: clamp(200px, 34vw, 300px);
    flex: 0 1 300px;
  }
`;

const NewChatBtn = styled.button`
  background: #e97024;
  border: none;
  border-radius: 12px;
  color: white;
  padding: 12px 16px;
  font-weight: 600;
  cursor: pointer;
  margin: 16px 16px 12px;
  transition: all 0.3s ease;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(233,112,36,0.2), 0 2px 8px rgba(0,0,0,0.08);
  flex-shrink: 0; 
  min-width: 0;   
`;


const SearchConversations = styled.div`
  margin: 0 16px 12px;
  position: relative;
  flex-shrink: 0;
  min-width: 0; 
`;

const SearchConversationsInput = styled.input`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box; 
  padding: 10px 16px 10px 36px;
  border: 2px solid #e1e5e9;
  border-radius: 10px;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  transition: all 0.3s ease;
  background: rgba(255,255,255,0.9);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);

  &:focus {
    outline: none;
    border-color: #e97024;
    box-shadow: 0 0 0 3px rgba(255,107,53,0.1), 0 4px 15px rgba(0,0,0,0.08);
    background: rgba(255,255,255,0.95);
  }
`;
const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 14px;
  width: 14px;
  height: 14px;
  &::before {
    content: '';
    position: absolute;
    width: 10px; height: 10px;
    border: 2px solid currentColor;
    border-radius: 50%;
    top: 0; left: 0;
  }
  &::after {
    content: '';
    position: absolute;
    width: 2px; height: 6px;
    background: currentColor;
    border-radius: 1px;
    top: 8px; left: 8px;
    transform: rotate(45deg);
  }
`;

const ConversationList = styled.div`
  flex: 1 1 auto;      
  min-height: 0;
  min-width: 0;        
  overflow-y: auto;
  padding: 0 16px 16px;
  position: relative;
  z-index: 20;
  direction: rtl;
  scrollbar-width: thin;
  scrollbar-color: rgba(233,112,36,0.3) rgba(0,0,0,0.05);

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: rgba(0,0,0,0.05); border-radius: 4px; margin: 8px 0; }
  &::-webkit-scrollbar-thumb { background: rgba(233,112,36,0.3); border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: rgba(233,112,36,0.5); }
`;

const ConversationItem = styled.div`
  direction: ltr;
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  position: relative;
  overflow: visible;
  box-shadow: 0 4px 15px rgba(0,0,0,0.06);
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255,107,53,0.1);
    transform: translateX(4px);
    border-color: rgba(255,107,53,0.2);
    box-shadow: 0 8px 30px rgba(0,0,0,0.12), 0 4px 20px rgba(255,107,53,0.1);
  }

  &.active {
    background: rgba(255,107,53,0.15);
    border-color: rgba(255,107,53,0.3);
    box-shadow: 0 6px 25px rgba(0,0,0,0.1), 0 2px 15px rgba(255,107,53,0.15);
  }
`;

const ConversationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`;
const ConversationTitle = styled.div`
  color: #333;
  font-weight: 600;
  font-size: 14px;
  flex: 1;
  margin-right: 8px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const ConversationDate = styled.div`
  color: #999;
  font-size: 11px;
  white-space: nowrap;
  font-weight: 400;
`;
const ConversationPreview = styled.div`
  color: #666;
  font-size: 12px;
  line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
`;

const DeleteBtn = styled.button`
  width: 20px; height: 20px;
  border-radius: 6px;
  background: transparent;
  border: none; color: #999;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; transition: all 0.3s ease;
  opacity: 0; transform: scale(0.8);
  position: absolute; bottom: 8px; right: 8px; z-index: 10;

  ${ConversationItem}:hover & { opacity: 1; transform: scale(1); }

  &:hover {
    background: rgba(239,68,68,0.1);
    color: #ef4444;
  }
`;

const ConversationTooltip = styled.div`
  position: fixed;
  background: white;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  z-index: 9999;
  width: 320px;
  opacity: 0; visibility: hidden; transform: translateY(-10px);
  transition: all 0.2s ease;
  pointer-events: none;
  backdrop-filter: blur(20px);
  direction: ltr;

  &.show { opacity: 1; visibility: visible; transform: translateY(0); }
`;
const TooltipTitle = styled.div`
  font-weight: 600; color: #333; margin-bottom: 8px; font-size: 14px;
`;
const TooltipContent = styled.div`
  color: #666; font-size: 13px; line-height: 1.4; margin-bottom: 8px;
`;
const TooltipMeta = styled.div`
  display: flex; justify-content: space-between;
  font-size: 11px; color: #999; border-top: 1px solid #f0f0f0; padding-top: 8px;
`;


const DocumentsPanel = styled.div`
  width: clamp(300px, 28vw, 450px);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 25px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.05);
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    flex-basis: 360px;
    min-width: 260px;
  }
  @media (max-width: 780px) {
    flex-basis: 100%;
    width: 100%;
  }
`;

const DocumentsHeaderX = styled.div`
  position: relative;
  padding: 24px;
  border-bottom: 1px solid rgba(0,0,0,0.1);
  flex-shrink: 0;
  h2 { font-size: 18px; font-weight: 700; color: #333; margin: 0 0 8px; }
  p { font-size: 14px; color: #666; margin: 0; }
`;

const SearchFilterRow = styled.div`
  margin: 12px 24px 16px;
  display: grid;
  grid-template-columns: 1fr 140px; 
  column-gap: 12px;
  align-items: stretch;
  min-width: 0;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    row-gap: 10px;
  }
`;


const SearchBox = styled.div`
  min-width: 0;       
  position: relative; 
`;

const DocsSearchInput = styled.input`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 12px 16px 12px 40px; 
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  font-size: 14px;
  background: rgba(255,255,255,0.9);
  backdrop-filter: blur(10px);
  transition: all .3s ease;
  &:focus {
    outline: none;
    border-color: #e97024;
    box-shadow: 0 0 0 3px rgba(255,107,53,0.1);
    background: rgba(255,255,255,0.95);
  }
`;

const SearchGlyph = styled.span`
  position: absolute;
  left: 12px; top: 50%; transform: translateY(-50%);
  width: 14px; height: 14px; color: #999; pointer-events: none;
  &::before { content:''; position:absolute; width:10px; height:10px; border:2px solid currentColor; border-radius:50%; }
  &::after  { content:''; position:absolute; width:2px; height:6px; background:currentColor; border-radius:1px; top:8px; left:8px; transform:rotate(45deg); }
`;

const FilterSelect = styled.select`
  width: 100%;
  min-width: 0;
  padding: 10px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 10px;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  background: #fff;
  box-sizing: border-box;
  transition: all .3s ease;

  &:focus {
    outline: none;
    border-color: #e97024;
    box-shadow: 0 0 0 3px rgba(255,107,53,0.1);
  }
`;

const DocumentsScroll = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 24px 24px;
  scrollbar-width: thin;
  scrollbar-color: rgba(233,112,36,0.3) rgba(0,0,0,0.05);
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: rgba(0,0,0,0.05); border-radius: 4px; margin: 8px 0; }
  &::-webkit-scrollbar-thumb { background: rgba(233,112,36,0.3); border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: rgba(233,112,36,0.5); }
`;

const DocSection = styled.div`
  margin-bottom: 32px;
`;

const SectionHeaderRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px; padding: 16px 0;
  border-bottom: 2px solid #f0f0f0;
  background: rgba(255,255,255,0.6);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const SectionTitle = styled.div`
  font-size: 16px; font-weight: 700; color: #333;
  display: flex; align-items: center; gap: 8px;
`;

const SectionIndicator = styled.div`
  width: 12px; height: 12px; border-radius: 50%;
  background: ${({ $selected }) => ($selected ? '#5db146' : '#e1e5e9')};
  ${({ $selected }) => $selected && `
    animation: pulse 2s infinite;
    @keyframes pulse { 0%,100%{opacity:1;} 50%{opacity:.7;} }
  `}
`;

const SectionCount = styled.span`
  background: rgba(93,177,70,0.1);
  color: #5db146;
  padding: 4px 8px; border-radius: 12px;
  font-size: 12px; font-weight: 600;
`;

const SectionStatus = styled.div`
  font-size: 12px; color: #5db146; font-weight: 600;
  background: rgba(93,177,70,0.1); padding: 4px 8px; border-radius: 12px;
`;

const DocItem = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 16px; border-radius: 12px; transition: all .3s ease;
  cursor: pointer; margin-bottom: 12px;
  background: ${({ $selected }) => ($selected ? 'rgba(93,177,70,0.08)' : 'rgba(255,255,255,0.8)')};
  border: 1px solid ${({ $selected }) => ($selected ? 'rgba(93,177,70,0.35)' : 'transparent')};
  box-shadow: 0 4px 15px rgba(0,0,0,0.06);
  position: relative; backdrop-filter: blur(10px);

  &:hover {
    background: ${({ $selected }) => ($selected ? 'rgba(93,177,70,0.12)' : 'rgba(255,107,53,0.08)')};
    transform: translateX(-4px);
    border-color: ${({ $selected }) => ($selected ? '#5db146' : 'rgba(255,107,53,0.2)')};
    box-shadow: 0 8px 30px rgba(0,0,0,0.12), 0 4px 20px ${({ $selected }) => ($selected ? 'rgba(93,177,70,0.2)' : 'rgba(255,107,53,0.1)')};
  }
`;

const DocIcon = styled.div`
  width: 30px; height: 30px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  color: white; font-weight: 700; font-size: 10px;
  ${({ $type }) => ({
    architectural: 'background:#3b82f6;',
    structural: 'background:#8b5cf6;',
    mechanical: 'background:#f59e0b;',
    contract: 'background:#ef4444;',
    client: 'background:#10b981;'
  }[$type] || 'background:#64748b;')}
`;

const DocInfo = styled.div`
  flex: 1; min-width: 0;
  .name {
    font-weight: 600; color: #333; font-size: 14px; margin-bottom: 2px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .meta { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
`;

const Tag = styled.span`
  background: ${({ $type }) => ({
    architectural: 'rgba(59,130,246,0.1);',
    structural: 'rgba(139,92,246,0.1);',
    mechanical: 'rgba(245,158,11,0.1);',
    contract: 'rgba(239,68,68,0.1);',
    client: 'rgba(16,185,129,0.1);'
  }[$type] || 'rgba(100,116,139,0.1)')};
  color: ${({ $type }) => ({
    architectural: '#3b82f6',
    structural: '#8b5cf6',
    mechanical: '#f59e0b',
    contract: '#ef4444',
    client: '#10b981'
  }[$type] || '#334155')};
  padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase;
`;

const Size = styled.span`
  color: #666; font-size: 12px;
`;

const DocStatus = styled.div`
  display: flex; align-items: center; gap: 8px;
`;

const TickBox = styled.div`
  width: 20px; height: 20px; border: 2px solid ${({ $checked }) => ($checked ? '#5db146' : '#ddd')};
  border-radius: 4px; cursor: pointer; transition: all .2s ease;
  display: flex; align-items: center; justify-content: center; font-size: 12px;
  background: ${({ $checked }) => ($checked ? '#5db146' : 'transparent')};
  color: ${({ $checked }) => ($checked ? 'white' : 'transparent')};
`;

const WelcomeStage = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(16px, 4vw, 48px);
`;

const WelcomeHero = styled.div`
  width: 100%;
  max-width: 920px;            
  text-align: center;
  margin: 0 auto;
  font-size: 0.9rem;
`;

const WelcomeTitle = styled.h2`
  margin: 0 0 12px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: #222;                
  font-size: clamp(22px, 4.5vw, 34px);
`;

const WelcomeSubtitle = styled.p`
  margin: 0 auto clamp(28px, 4.8vw, 48px);
  max-width: 740px;
  color: #666;
  font-size: clamp(12px, 1.6vw, 16px);
  line-height: 1.6;
`;

const WelcomePrompt = styled.div`
  position: relative;
  margin: 0 auto;
  width: min(860px, 92%);
  background: #fff;                    
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.06), 0 6px 20px rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.06);
  padding: 6px;                        
  display: flex;
  align-items: stretch;

  &:focus-within {                     
    border-color: #ffd7c3;
    box-shadow: 0 0 0 4px rgba(255,107,53,0.08),
                0 20px 60px rgba(0,0,0,0.06),
                0 6px 20px rgba(0,0,0,0.05);
  }
`;

const WelcomeInput = styled.textarea`
  flex: 1;
  width: 100%;
  border: 2px solid transparent;
  background: white;
  border-radius: 6px;          
  padding: 10px 10px;
  font-family: inherit;  
  font-size: 16px;          
  line-height: 1.3;            
  min-height: 20px;           
  max-height: 100px;           
  resize: none;
  outline: none;
  color: #1f2937;
  box-shadow: none;

  &::placeholder { color: #a3a3a3; }
`;



const WelcomeSend = styled.button`
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 12px;
  background: #e97024;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
  box-shadow: 0 10px 24px rgba(233,112,36,0.22), 0 6px 10px rgba(0,0,0,0.08);

  &:hover { transform: scale(1.05); background: #ff7628; }
  &:active { transform: scale(0.98); }
`;


const WelcomeActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;           
  padding-right: 6px;  
`;

const ActionWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const UploadBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background: #f59e0b;
  color: white;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 1px 6px rgba(0,0,0,.18);
`;


const FeedbackBar = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 6px;
  justify-content: flex-end;

  button {
    border: none;
    border-radius: 10px;
    padding: 6px 10px;
    cursor: pointer;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: background .15s, color .15s, box-shadow .15s, opacity .15s;
    background: #fff;
  }

  .up { color: #16a34a; background: rgba(22,163,74,0.10); }
  .down { color: #ef4444; background: rgba(239,68,68,0.10); }

  .up.active {
    background: rgba(22,163,74,0.18);
    box-shadow: inset 0 0 0 1px rgba(22,163,74,0.35);
  }
  .down.active {
    background: rgba(239,68,68,0.18);
    box-shadow: inset 0 0 0 1px rgba(239,68,68,0.35);
  }

  .inactive {
    color: #111827;
    background: #fff;
    box-shadow: inset 0 0 0 1px #e5e7eb;
  }

  button:hover { filter: brightness(0.97); }
  button:disabled { opacity: .98; cursor: default; }
`;

const WebModeButton = styled.button`
  position: relative;
  width: 40px;
  height: 40px;
  border: 1px solid #ffd7c3;
  background: ${({ $active }) => ($active ? '#ffefe7' : '#fff')};
  color: #e97024;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  transition: all .2s ease;
  z-index: 1000;
  &:hover { background: #ffe6db; }

  &::after {
    content: attr(data-title);
    position: absolute;
    bottom: 48px;
    left: 50%;
    transform: translateX(-50%) translateY(2px);
    background: #ffffff;                   
    color: #111827;                       
    font-size: 12px;
    line-height: 1;
    padding: 8px 10px;
    border-radius: 8px;
    white-space: nowrap;
    border: 1px solid rgba(0,0,0,0.1);    
    box-shadow: 0 8px 20px rgba(0,0,0,0.12);
    opacity: 0;
    pointer-events: none;
    transition: opacity .15s ease, transform .15s ease;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #ffffff;             
    opacity: 0;
    transition: opacity .15s ease;
  }

  &:hover::after,
  &:hover::before,
  &:focus-visible::after,
  &:focus-visible::before {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

const ToggleDetailsBtn = styled.button`
  border: none;
  border-radius: 10px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background .15s, color .15s, box-shadow .15s, opacity .15s;
  background: #fff;
  color: #111827;
  box-shadow: inset 0 0 0 1px #e5e7eb;

  &.active {
    background: rgba(59,130,246,0.14);
    box-shadow: inset 0 0 0 1px rgba(59,130,246,0.35);
    color: #1f4fbf;
  }
`;

const ReferencesWrap = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  box-shadow: inset 0 0 0 1px #e5e7eb;

  max-height: 280px;

  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
`;

const RefList = styled.div`
  display: grid;
  gap: 10px;
  overflow-y: auto;     
  padding-right: 4px;   
`;

const RefSnippet = styled.div`
  font-size: 12px;
  color: #374151;
  line-height: 1.4;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;

  max-height: 140px;
  overflow-y: auto;

  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
`;




const RefHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 6px;
  font-size: 13px; color: #111827; font-weight: 600;
`;



const RefItem = styled.div`
  display: grid; gap: 4px;
`;

const RefTitle = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 600; color: #1f2937;
  svg { width: 14px; height: 14px; color: #3b82f6; }
`;

const PillsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 6px;

  max-height: 120px;
  overflow-y: auto;
  padding-right: 4px;
`;

const ReferencePill = styled.div`
  font-size: 11px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #111827;
  border-radius: 8px;
  padding: 6px 8px;

  white-space: normal;
  line-height: 1.25;
  max-height: 60px;
  overflow-y: auto;

  overflow-wrap: anywhere;
  word-break: break-word;

  ${({ $clickable }) =>
    $clickable &&
    `
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s;

      &:hover {
        background: #eef2ff;
        border-color: #c7d2fe;
      }
    `}
`;


const LoadingStrip = styled.div`
  height: 4px;
  width: 100%;
  background: #ffe6db;
  overflow: hidden;
  position: relative;
  border-radius: 999px;
  margin: 8px 0 0;
`;

const LoadingThumb = styled.div`
  position: absolute;
  top: 0;
  left: -40%;
  height: 100%;
  width: 40%;
  background: #e97024;
  border-radius: 999px;
  animation: slide 1.1s ease-in-out infinite;

  @keyframes slide {
    0%   { left: -40%; width: 40%; }
    50%  { left: 30%;  width: 50%; }
    100% { left: 100%; width: 40%; }
  }
`;

const SkeletonItem = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 16px; border-radius: 12px; margin-bottom: 12px;
  background: rgba(255,255,255,0.8); border: 1px solid transparent; position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(0,0,0,0.05), transparent);
    transform: translateX(-100%);
    animation: shimmer 1.2s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const SkeletonIcon = styled.div`
  width: 30px; height: 30px; border-radius: 8px; background: #eee;
`;

const SkeletonText = styled.div`
  flex: 1;
  .line1 { height: 12px; background:#eee; border-radius: 6px; width: 60%; margin-bottom: 8px; }
  .line2 { height: 10px; background:#f0f0f0; border-radius: 6px; width: 30%; }
`;

const DocsPillsRow = styled.div`
  margin-top: 6px;
  margin-bottom: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const DocPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #312e81;
  border: 1px solid #c7d2fe;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  &:hover {
    ${({ $clickable }) =>
      $clickable &&
      `
        background: #e0e7ff;
      `}
  }

  svg {
    flex-shrink: 0;
  }

  .title {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .page {
    font-weight: 600;
    margin-left: 2px;
  }
`;
const SideShell = styled.div`
  align-self: stretch;
  min-height: 0;
  display: flex;
  position: relative;
  overflow: hidden;
  min-width: 0;
  flex: 0 0 auto;

  width: ${({ $collapsed, $expandedWidth, $railWidth }) =>
    $collapsed ? `${$railWidth}px` : $expandedWidth};
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);

  border-right: ${({ $side }) =>
    $side === 'left' ? '1px solid rgba(0,0,0,0.1)' : 'none'};
  border-left: ${({ $side }) =>
    $side === 'right' ? '1px solid rgba(0,0,0,0.1)' : 'none'};
`;

const SideInner = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  min-width: 0;
  display: flex;
  transition: opacity 0.15s ease;

  ${({ $collapsed }) =>
    $collapsed &&
    `
    opacity: 0;
    pointer-events: none;
    position: absolute;
    inset: 0;
  `}
`;

const SideRail = styled.button`
  display: ${({ $collapsed }) => ($collapsed ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  width: ${({ $railWidth }) => `${$railWidth}px`};
  height: 100%;
  flex-shrink: 0;
  padding: 12px 0 0;
  margin: 0;
  border: none;
  background: #fff;
  color: #e97024;
  cursor: pointer;
  appearance: none;
  transition: background 0.15s ease;

  &:hover {
    background: #ffefe7;
  }

  &:focus-visible {
    outline: 2px solid #e97024;
    outline-offset: -2px;
  }
`;

const RailIcon = styled.span`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  pointer-events: none;
`;

const EdgeGrip = styled.span`
  width: 6px;
  height: 100%;
  background: transparent;
  border-radius: 3px;
  opacity: 0;
  box-shadow: none;
  transition: opacity 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
  pointer-events: none;
`;

const EdgeGripZone = styled.button`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 20px;
  z-index: 20;
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'flex')};
  align-items: stretch;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: default;
  appearance: none;

  ${({ $side }) =>
    $side === 'left'
      ? 'right: 0; justify-content: flex-end;'
      : 'left: 0; justify-content: flex-start;'}

  &:hover ${EdgeGrip},
  &:focus-visible ${EdgeGrip} {
    opacity: 1;
    background: #e97024;
    box-shadow: 0 0 10px rgba(233, 112, 36, 0.55), 0 0 20px rgba(233, 112, 36, 0.25);
  }

  &:focus-visible {
    outline: none;
  }
`;



const TempUploadChips = ({ items = [] }) => {
  if (!items || items.length === 0) return null;

  const Bar = ({ children }) => (
    <div style={{
      height: 6,
      background: '#ffe6db',
      borderRadius: 999,
      overflow: 'hidden',
      position: 'relative',
      marginTop: 6
    }}>
      {children}
    </div>
  );

  const BarFill = ({ percent = 0 }) => (
    <div style={{
      height: '100%',
      width: `${Math.max(0, Math.min(100, percent))}%`,
      background: '#e97024',
      borderRadius: 999,
      transition: 'width 120ms ease'
    }} />
  );

  const BarIndeterminate = () => (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <div style={{
        content: '""',
        position: 'absolute',
        left: '-40%',
        top: 0,
        width: '40%',
        height: '100%',
        background: '#7c3aed',
        borderRadius: 999,
        animation: 'slideInd 1.2s infinite'
      }} />
      <style>
        {`@keyframes slideInd {
          0% { left: -40%; }
          50% { left: 60%; }
          100% { left: 110%; }
        }`}
      </style>
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px 12px',
        padding: '8px 0 0',
        fontSize: 12,
        lineHeight: 1.4,
        color: '#444',
        justifyContent: 'center'
      }}
    >
      {items.map(doc => {
        const color =
          doc.status === 'ready' ? '#16a34a' :
          doc.status === 'error' ? '#dc2626' :
          doc.status === 'processing' ? '#7c3aed' : 
          '#b45309'; 

        return (
          <div
            key={doc.tempDocId}
            style={{
              background: '#fff7ed',
              border: '1px solid #fdba74',
              borderRadius: 10,
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              minWidth: 220,
              maxWidth: 260
            }}
            title={doc.name}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  color: '#111827'
                }}
              >
                {doc.name}
              </span>
              <span style={{ fontSize: 11, color }}>
                {doc.status === 'ready' && 'ready'}
                {doc.status === 'error' && 'error'}
                {doc.status === 'processing' && 'processing…'}
                {doc.status === 'uploading' && `uploading ${Math.max(0, Math.min(100, doc.progress ?? 0))}%`}
              </span>
            </div>

            {doc.status === 'uploading' && (
              <Bar><BarFill percent={doc.progress ?? 0} /></Bar>
            )}
            {doc.status === 'processing' && (
              <Bar><BarIndeterminate /></Bar>
            )}
          </div>
        );
      })}
    </div>
  );
};

const UploadToast = ({ open, text, onClose }) => {
  if (!open) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        background: '#111827',
        color: 'white',
        padding: '10px 14px',
        borderRadius: 10,
        boxShadow: '0 10px 24px rgba(0,0,0,.25)',
        zIndex: 2000,
        maxWidth: 360
      }}
      onClick={onClose}
      title="Dismiss"
    >
      {text}
    </div>
  );
};

const FancyWelcome = React.memo(function FancyWelcome({
  inputValue,
  setInputValue,
  onSend,
  onAttach,
  tempDocsUploading,
  onWebToggle,
  isWeb,
  webTooltip,
  onVoiceClick,
  isRecording,
  disableSend,
  showUploadToast,
  canSend,
  hasProcessingUploads,
  sandboxMode,
}) {

  const { t } = useFlexibleTranslation();
  const taRef = React.useRef(null);

  React.useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 150) + 'px';
  }, [inputValue]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      if (hasProcessingUploads) {
        showUploadToast?.('Your file is still processing.');
        return;
      }

      if (!canSend) return;

      onSend();
    }
  };


  return (
    <WelcomeStage>
      <WelcomeHero>
        <WelcomeTitle>
          {t('dashboardChatbot.howCanCodiiiHelp', 'How can CODiii help you today?')}
        </WelcomeTitle>
        <WelcomeSubtitle>
          {t('dashboardChatbot.selectDocumentsSubtitle', 'Select the documents you have questions for on the right and begin your research with me here')}
        </WelcomeSubtitle>

        <WelcomePrompt>
          <WelcomeInput
            ref={taRef}
            autoFocus
            rows={1}
            placeholder={t('dashboardChatbot.typeMessage', 'Type your message...')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <WelcomeActions>
            {/* Attach + badge */}
            {!sandboxMode && (
              <ActionWrap>
                <AttachButton
                  type="button"
                  onClick={onAttach}
                  title={t('dashboardChatbot.attachAFile', 'Attach a file for this chat')}
                  aria-label={t('dashboardChatbot.attachAFile', 'Attach a file for this chat')}
                  data-title={t('dashboardChatbot.attachFile', 'Attach a file')}
                >
                  <FiPaperclip size={18} />
                </AttachButton>

                {tempDocsUploading.some(d => d.status === 'processing' || d.status === 'uploading') && (
                  <UploadBadge aria-label="Uploads in progress">
                    {tempDocsUploading.filter(d => d.status === 'processing').length}
                  </UploadBadge>
                )}
              </ActionWrap>
            )}


            {/* Web toggle */}
            {!sandboxMode && (
              <WebModeButton
                type="button"
                $active={isWeb}
                data-title={webTooltip}
                aria-label={webTooltip}
                title={webTooltip}
                aria-pressed={isWeb}
                onClick={onWebToggle}
              >
                {isWeb ? <FiFileText size={18} /> : <FiGlobe size={18} />}
              </WebModeButton>
            )}

            {/* Voice */}
            <VoiceButton
              type="button"
              onClick={onVoiceClick}
              isRecording={isRecording}
              title={isRecording ? 'Stop voice input' : 'Start voice input'}
              aria-label={isRecording ? 'Stop voice input' : 'Start voice input'}
            >
              <i className="fas fa-microphone" />
            </VoiceButton>

            {/* Send */}
            <WelcomeSend
              onClick={onSend}
              disabled={!inputValue.trim() || disableSend}
              aria-label="Send"
              title={disableSend ? 'Disabled while your file is processing' : 'Send'}
            >
              →
            </WelcomeSend>
          </WelcomeActions>
        </WelcomePrompt>


        {tempDocsUploading.length > 0 && (
          <div style={{ marginTop: 12, width: '100%' }}>
            <div
              style={{
                marginBottom: 4,
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '.04em',
                color: '#6b7280',
                fontWeight: 600,
              }}
            >
              Temporary files for this chat
            </div>
            <TempUploadChips items={tempDocsUploading} />
          </div>
        )}
      </WelcomeHero>
    </WelcomeStage>
  );
});


const DashboardChatbot = ({ 
  theme,
  currentPage,
  projectId,
  selectedRequirement,
  sandboxMode,
  allRequirements = []
}) => {
  const { t } = useFlexibleTranslation();
  const documentCategories = [
    { id: 'all', label: t('dashboardChatbot.allDocuments', 'All Documents') },
    { id: 'technical', label: t('dashboardChatbot.technical', 'Technical') },
    { id: 'regulatory', label: t('dashboardChatbot.regulatory', 'Regulatory') },
    { id: 'contractual', label: t('dashboardChatbot.contractual', 'Contractual') },
    { id: 'other', label: t('dashboardChatbot.other', 'Other') }
  ];
  const API_URL = process.env.REACT_APP_API_URL_CHATBOT;
  const CODES_API_BASE = process.env.REACT_APP_CODES_API_BASE; 
  const PDF_SIGNED_URL_API = process.env.REACT_APP_PDF_SIGNED_URL_API;


  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingSteps, setProcessingSteps] = useState({
    analyzing: false,
    filtering: false,
    preparing: false
  });
  const [showDocumentSearch, setShowDocumentSearch] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [showRequirementsDialog, setShowRequirementsDialog] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);
  const [filteredRequirementsCount, setFilteredRequirementsCount] = useState(0);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [filteredCards, setFilteredCards] = useState([]);
  const [referencedDocuments, setReferencedDocuments] = useState([]);
  const [activeDocuments, setActiveDocuments] = useState([]);
  const [initialPrompt, setInitialPrompt] = useState('');
  // Map<botMsgId, number> — query-sent timestamp, read back when the poll
  // completes to compute chatbot_response's duration_ms. Not persisted;
  // fine to lose on reload since a resumed poll isn't a fresh "how long did
  // this response take" measurement anyway.
  const queryStartTimesRef = useRef({});
  const chatContainerRef = useRef(null);
  const lastSelectedBlock = useRef(null);
  const isDraggingRef = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const startSize = useRef({ width: 0, height: 0 });
  const messagesEndRef = useRef(null);
  const [sidePanelSearch, setSidePanelSearch] = useState('');
  const [lastBotMessage, setLastBotMessage] = useState(null);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [webTip, setWebTip] = useState({ show: false, x: 0, y: 0, text: '' });
  const [companyId, setCompanyId] = useState(null);

  const [historySearch, setHistorySearch] = useState('');
  const [tooltip, setTooltip] = useState({
    show: false,
    x: 0,
    y: 0,
    title: '',
    preview: '',
    metaLeft: '',
    metaRight: ''
  });

  const [chatMode, setChatMode] = useState('project'); 

  const [gptConversations, setGptConversations] = useState([]);
  const [gptActiveId, setGptActiveId] = useState(null);
  const [gptMessages, setGptMessages] = useState([]);
  const [gptSearch, setGptSearch] = useState('');
  const [gptIsSending, setGptIsSending] = useState(false);

  const [tempFile, setTempFile] = useState(null); 
  const [tempUploadInitResult, setTempUploadInitResult] = useState(null); 

  const [tempDocsUploading, setTempDocsUploading] = useState([]);

  const hasProcessingUploads = useMemo(
    () =>
      chatMode !== 'gpt' &&
      tempDocsUploading.some(
        d => d.status === 'processing' || d.status === 'uploading'
      ),
    [chatMode, tempDocsUploading]
  );


  const patchTempUpload = useCallback((tempDocId, patch) => {
    setTempDocsUploading(prev =>
      prev.map(doc => (doc.tempDocId === tempDocId ? { ...doc, ...patch } : doc))
    );
  }, []);

  const fileInputRef = useRef(null);
  const [uploadToast, setUploadToast] = useState({ open: false, text: '' });
  const showUploadToast = (text) => setUploadToast({ open: true, text });
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: null,
    initialPage: 1,
    title: '',
    highlightText: null,  
  });
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  const toggleLeft = useCallback(() => setLeftCollapsed(v => !v), []);
  const toggleRight = useCallback(() => setRightCollapsed(v => !v), []);

  const docType = (doc) => {
    const t = (doc.category || doc.type || '').toLowerCase();
    if (['architectural','structural','mechanical','contract','client'].includes(t)) return t;
    return 'architectural'; 
  };
  const hasRefs = (msg) => Array.isArray(msg?.references) && msg.references.length > 0;


  const currentChat = useMemo(
    () => chatHistory.find(c => String(c.id) === String(activeChat)),
    [chatHistory, activeChat]
  );
  const selectedDocs = currentChat?.selectedDocuments || [];

  const isStockWelcome =
    messages.length === 1 &&
    !messages[0]?.isUser &&
    typeof messages[0]?.text === 'string' &&
    /How can CODiii help you today\?/i.test(messages[0].text);

    const showWelcomeLayout = isStockWelcome && selectedDocs.length === 0;

  const showTooltip = (e, chat) => {
    const firstTwo = (chat.messages || [])
      .filter(Boolean)
      .slice(0, 2)
      .map(m => (m?.text || '').replace(/\s+/g, ' ').trim())
      .join(' • ');
    setTooltip({
      show: true,
      x: e.clientX + 16,
      y: e.clientY + 16,
      title: chat.title,
      preview: firstTwo || '—',
      metaLeft: new Date(chat.timestamp || Date.now()).toLocaleString(),
      metaRight: `${(chat.messages || []).length} msgs`
    });
  };
  const moveTooltip = (e) => setTooltip(t => ({ ...t, x: e.clientX + 16, y: e.clientY + 16 }));
  const hideTooltip = () => setTooltip(t => ({ ...t, show: false }));

  const auth = useAuth();
  useEffect(() => {
    let cancelled = false;

    const loadCompany = async () => {
      if (!auth?.isAuthenticated || !auth?.user?.profile?.sub) {
        setCompanyId(null);
        return;
      }

      try {
        const payload = await CompanyService.getMyCompany();

        if (!cancelled) {
          setCompanyId(payload?.company?.id || null);
        }
      } catch (err) {
        console.error('Failed to load company for chatbot:', err);
        if (!cancelled) {
          setCompanyId(null);
        }
      }
    };

    loadCompany();

    return () => {
      cancelled = true;
    };
  }, [auth?.isAuthenticated, auth?.user?.profile?.sub]);

  // === STALE SELECTED DOCS FIX ===
  // If a folder/link gets deleted, backend doc list updates, but chat.selectedDocuments can be stale.
  // We prune anything not present in the latest filteredDocuments list.
  const pruneSelectedDocsForActiveChat = useCallback(
    (validIds, { silent = false } = {}) => {
      if (!activeChat || !validIds) return;

      setChatHistory(prev => {
        return prev.map(c => {
          if (String(c.id) !== String(activeChat)) return c;

          const current = Array.isArray(c.selectedDocuments) ? c.selectedDocuments : [];
          const removed = current.filter(d => !validIds.has(d.id));
          if (removed.length === 0) return c;

          const next = current.filter(d => validIds.has(d.id));

          // persist the pruned selection
          saveChatSession(
            c.id,
            c.title,
            c.messages,
            projectId,
            next
          );

          // keep local selection state in sync
          setSelectedDocuments(next);
          setActiveDocuments(next);

          if (!silent) {
            const names = removed
              .map(d => d?.title || d?.name || d?.id)
              .filter(Boolean)
              .slice(0, 3)
              .join(', ');
            showUploadToast(
              removed.length === 1
                ? `A selected document is no longer available and was removed: ${names}`
                : `${removed.length} selected documents are no longer available and were removed: ${names}${removed.length > 3 ? '…' : ''}`
            );
          }

          return { ...c, selectedDocuments: next, timestamp: new Date() };
        });
      });
    },
    [activeChat, auth?.user?.profile?.sub, projectId]
  );

  useEffect(() => {
    if (!activeChat) return;
    if (!Array.isArray(filteredDocuments)) return;
    if (sidebarLoading) return; 
    const validIds = new Set(filteredDocuments.map(d => d.id));
    pruneSelectedDocsForActiveChat(validIds);
  }, [activeChat, filteredDocuments, sidebarLoading, pruneSelectedDocsForActiveChat]);

  useEffect(() => {
    if (!activeChat) {
      return;
    }

    const loadTempDocs = async () => {
      try {
        const res = await fetch(
          `${TEMP_UPLOAD_API_BASE}/chatbotTempUpload/list?chatId=${activeChat}`
        );
        const data = await res.json();

        setTempDocsUploading(
          (data.tempDocs || []).map(d => ({
            tempDocId: d.SK.replace('TEMPDOC#', ''),
            name: d.fileName,
            status: 'ready',
            progress: 100
          }))
        );
      } catch (err) {
        console.error("Failed to load temp docs", err);
      }
    };

    loadTempDocs();
  }, [activeChat]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
    }
  }, [auth.isAuthenticated, auth.user]);
  useEffect(() => {
    if (chatMode !== 'gpt') return;
    (async () => {
      try {
        const list = await gptListConversations(auth);
        setGptConversations(list);
        if (!gptActiveId && list.length > 0) {
          const id = list[0].conversationId;
          setGptActiveId(id);
          const hist = await gptGetHistory(id, auth);
          setGptMessages(
            hist.map(m => ({
              text: m.content,
              isUser: m.role === 'user',
              timestamp: new Date(m.createdAt),
              fromLLM: m.role === 'assistant',
              fromGPT: true,
            }))
          );
        }
      } catch (e) {
        console.error('GPT list failed:', e);
      }
    })();
  }, [chatMode]); 

  

  useEffect(() => {
    if (
      currentPage === 'dashboard' &&
      activeChat &&
      Array.isArray(chatHistory)
    ) {
      const chat = chatHistory.find(c => String(c.id) === String(activeChat));
      if (chat && Array.isArray(chat.messages)) {
        setMessages(structuredClone(chat.messages || []));
      }
    }
  }, [currentPage, activeChat, chatHistory]);
    useEffect(() => {
      setFilteredDocuments([]);
      setSelectedDocuments([]);
      setActiveDocuments([]);
      setReferencedDocuments([]);
      setSidePanelSearch('');
      
      if (!projectId && !companyId) {
        setSidebarLoading(false);
      } else {
        setSidebarLoading(true);
      }
    }, [projectId, companyId]);
  
  const reloadFilteredDocuments = useCallback(async () => {
  if (!projectId && !companyId) return [];

  try {
    setSidebarLoading(true);

    const res = await fetch(`${API_URL}/chatbotDocumentDisplay`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId, companyId })
    });

    if (!res.ok) {
      console.warn(`[DashboardChatbot] chatbotDocumentDisplay ${res.status} — skipping doc sidebar`);
      setSidebarLoading(false);
      return [];
    }

    const docs = await res.json();

    const strippedDocuments = (docs.documents || []).map(doc => ({
      title: doc.title,
      bucket: doc.s3Bucket,
      s3Key: doc.s3Key,
      originalPdfKey: doc.originalPdfKey || doc.originalS3Key || null,
      category: doc.category || 'technical',
      id: doc.id || doc.requirementId || doc.s3Key,
      scopeType: doc.scopeType || 'PROJECT',
      projectId: doc.projectId || projectId || null,
      companyId: doc.companyId || companyId || null,
      resourceId: doc.resourceId || null,
      linkId: doc.linkId || null,
      fileType: doc.fileType || null,
    }));

    let latest = strippedDocuments;
    if (strippedDocuments.length > 0 && (projectId || companyId)) {
      try {
        const resultRes = await fetch(`${API_URL}/chatbotDocumentDisplayResults`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documents: strippedDocuments,
            context: { projectId, companyId }
          })
        });
        if (resultRes.ok) {
          const resultData = await resultRes.json();
          if (resultData?.documents?.length) latest = resultData.documents;
        }
      } catch (_err) {
        // Fall back to raw stripped list on any network/API error.
      }
    }

    setFilteredDocuments(latest);
    setSidebarLoading(false);
    return latest;
  } catch (err) {
    setSidebarLoading(false);
    console.error("Failed to load and refine documents:", err);
    return [];
  }
}, [projectId, companyId, API_URL]);

    useEffect(() => {
    const initChat = async () => {
      if ((!projectId && !companyId) || !auth?.user?.profile?.sub) {
        return;
    }

      try {
        const chats = await loadChatSessions(projectId);

        if (chats.length > 0) {
          const sortedChats = chats.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          const lastChat = sortedChats[0];

          setChatHistory(chats);
          setActiveChat(lastChat.id);
          setMessages([...lastChat.messages]);
          const pendingMsgs = (lastChat.messages || []).filter(
            m => m?.fromLLM && m?.status === 'pending'
          );

          const pendingReqs = readPendingReqs(projectId, lastChat.id);

          for (const m of pendingMsgs) {
            if (m.jobId) {
              startPolling(m.jobId, m.id, lastChat.id, lastChat.selectedDocuments || []);
              continue;
            }

            const saved = pendingReqs?.[m.id];
            if (!saved) continue;

            try {
              const res = await fetch(`${API_URL}/requirementsChatbotChat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  chatId: lastChat.id,
                  messages: saved.outgoingMessages,
                  documents: saved.docsNow,
                  tempDocuments: saved.readyTempDocs,
                  context: saved.context,
                }),
              });

              if (!res.ok) throw new Error(await res.text());
              const { jobId } = await res.json();

              setMessages(prev => prev.map(x => (x.id === m.id ? { ...x, jobId } : x)));

              upsertPendingReq(projectId, lastChat.id, m.id, { jobId });
              startPolling(jobId, m.id, lastChat.id, lastChat.selectedDocuments || []);
            } catch (err) {
              console.error("Failed to recover pending job:", err);
            }
          }
          setSelectedDocuments(lastChat.selectedDocuments || []);
          setActiveDocuments(lastChat.selectedDocuments || []);
        } else {
          const welcomeMessage = {
            id: crypto.randomUUID(),
            text: t('dashboardChatbot.welcomeMessage', 'How can CODiii help you today? Select the documents you have questions for on the right and begin your research with me here'),
            isUser: false,
            timestamp: new Date()
          };

          const newChatId = crypto.randomUUID();
          setMessages([welcomeMessage]);
          setActiveChat(newChatId);
          setChatHistory([{
            id: newChatId,
            title: 'New Chat',
            messages: [welcomeMessage],
            timestamp: new Date()
          }]);

          try {
            await saveChatSession(
              newChatId,
              'New Chat',
              [welcomeMessage],
              projectId,
              []
            );
          } catch (err) {
            console.error('Failed to save initial chat session:', err);
          }

          setSelectedDocuments([]);
          setFilteredDocuments([]);
          setActiveDocuments([]);
          setAwaitingConfirmation(true);
          setReferencedDocuments([]);
          setShowDocumentDialog(false);
          setShowRequirementsDialog(false);
          setSearchQuery('');
          setInputValue('');
          setIsProcessing(false);
          setProcessingSteps({ analyzing: false, filtering: false, preparing: false });
        }

        if (projectId) {
          await reloadFilteredDocuments();
        }
      } catch (err) {
        console.error("Failed to initialize chat:", err);
      }
    };

    initChat();
    }, [projectId, companyId, auth?.user?.profile?.sub]);

  
  // Marks the original pending bot bubble as failed in place (instead of leaving
  // it stuck or appending a separate, redundant error message below it).
  const markMessageFailed = (chatIdToUse, botMsgId, docsNow, reasonText, reasonCode = 'unknown') => {
    removePendingReq(projectId, chatIdToUse, botMsgId);
    phChatbotResponseFailed({ projectId, reason: reasonCode, errorMessage: reasonText });

    setMessages(prev => {
      const next = prev.map(m =>
        m.id === botMsgId
          ? { ...m, status: "failed", text: reasonText, timestamp: new Date() }
          : m
      );

      setChatHistory(history => {
        const updated = history.map(chat =>
          String(chat.id) === String(chatIdToUse)
            ? { ...chat, messages: next, timestamp: new Date() }
            : chat
        );

        const current = updated.find(c => String(c.id) === String(chatIdToUse));
        if (current) {
          saveChatSession(chatIdToUse, current.title, next, projectId, docsNow);
        }

        return updated;
      });

      return next;
    });
  };

  const startPolling = async (jobId, botMsgId, chatIdToUse, docsNow) => {
    setProcessingSteps({ analyzing: false, filtering: false, preparing: true });

    const applyPollProgress = (pollData) => {
      const progressMessage = resolveResearchProgressMessage(pollData);
      setMessages(prev => prev.map(m =>
        m.id === botMsgId
          ? { ...m, progressMessage, researchStage: pollData?.stage || null }
          : m
      ));
    };

    let result = null;
    let retries = 0;
    const MAX_RETRIES = 60; // ~2 minutes at 2s/poll — was 450 (~15 min)

    while (retries < MAX_RETRIES) {
      const pollRes = await fetch(`${API_URL}/requirementsChatbot-polling?jobId=${jobId}`);
      const pollData = await pollRes.json();

      if (pollData.status === 'completed') {
        result = pollData;
        break;
      } else if (pollData.status === 'failed') {
        const reason = (pollData.response || pollData.message || '').trim();
        markMessageFailed(
          chatIdToUse,
          botMsgId,
          docsNow,
          reason || "Sorry, I couldn't generate a response for that. One of your selected documents may no longer be available — please check your document selection and try again.",
          'job_failed'
        );
        return;
      }

      applyPollProgress(pollData);

      await new Promise(resolve => setTimeout(resolve, 2000));
      retries++;
    }

    if (!result) {
      markMessageFailed(
        chatIdToUse,
        botMsgId,
        docsNow,
        "This is taking longer than expected and I wasn't able to get a response in time. Please check that your selected documents are still connected, then try again.",
        'timeout'
      );
      return;
    }

    const shortText = result.response ?? result.message ?? '';
    const longText  = result.detailedResponse ?? result.detailedMessage ?? '';
    const hasDetailed = !!longText && longText.trim().length > 0;

    const references = (() => {
      const out = [];

      // 1) From result.documents 
      if (Array.isArray(result.documents)) {
        for (const d of result.documents) {
          const c0 = (d.chunks && d.chunks[0]) || {};
          out.push({
            id: d.id,
            title: d.title || 'Document',
            snippet: (c0.content || '').trim(),
            pageNumber:
              c0.pageNumber ??
              c0.page ??
              d.pageNumber ??
              null, 
            score: c0.score ?? d.score ?? null,
            references: Array.isArray(c0.references) ? c0.references : [],
          });
        }
      }

      // 2) Fallback / merge from retrieved_chunks 
      if (Array.isArray(result.retrieved_chunks) && result.retrieved_chunks.length > 0) {
        for (const rc of result.retrieved_chunks) {
          const existing = out.find(x => x.id === rc.documentId);
          const rcRefs = Array.isArray(rc.references) ? rc.references : [];
          const rcSnippet = (rc.text || rc.rawContent || '').trim();
          const rcPage =
            rc.pageNumber ??
            rc.page ??
            (rc.metadata && (rc.metadata.pageNumber || rc.metadata.page)) ??
            null;

          if (existing) {
            const seen = new Set(
              (existing.references || []).map(r => `${r.type}|${(r.value || '').toLowerCase()}`)
            );
            for (const r of rcRefs) {
              const key = `${r.type}|${(r.value || '').toLowerCase()}`;
              if (!seen.has(key)) existing.references.push(r);
            }

            if (!existing.snippet && rcSnippet) existing.snippet = rcSnippet;

            if (existing.pageNumber == null && rcPage != null) {
              existing.pageNumber = rcPage;
            }

            if (rc.score != null) {
              existing.score =
                existing.score == null ? rc.score : Math.max(existing.score, rc.score);
            }
          } else {
            out.push({
              id: rc.documentId,
              title: rc.documentTitle || 'Document',
              snippet: rcSnippet,
              pageNumber: rcPage,
              score: rc.score ?? null,
              references: rcRefs,
            });
          }
        }
      }

      return out.filter(
        r => r.snippet || (Array.isArray(r.references) && r.references.length)
      );
    })();

    const botMessage = {
      id: crypto.randomUUID(),
      text: shortText,        
      shortText,
      longText,
      hasDetailed,
      isDetailed: false,       
      isUser: false,
      timestamp: new Date(),
      fromLLM: true,
      references,              
      showRefs: false          
    };
 
    setLastBotMessage(botMessage);

    const queryStartedAt = queryStartTimesRef.current[botMsgId];
    delete queryStartTimesRef.current[botMsgId];
    const citationCount = references.reduce(
      (sum, r) => sum + (Array.isArray(r.references) ? r.references.length : 0),
      0
    );
    phChatbotResponse({
      projectId,
      durationMs: queryStartedAt ? Date.now() - queryStartedAt : null,
      citationCount,
    });
    phChatbotFindingGenerated({
      projectId,
      // result.response_outcome comes from requirementsChatbotChat directly —
      // 'answered' | 'not_found_in_selected_documents' | 'more_information_needed' |
      // 'conflicting_information'. Falls back to a shortText-based guess only
      // for job rows written before this field existed.
      responseOutcome: result.response_outcome || (shortText.trim() ? 'answered' : 'not_found_in_selected_documents'),
      citationCount,
      docCountInScope: docsNow?.length,
    });

    setMessages(prev => {
      const next = prev.map(m => {
        if (m.id !== botMsgId) return m;

        return {
          ...m,
          status: "completed",
          text: shortText,
          shortText,
          longText,
          hasDetailed,
          isDetailed: false,
          references,
          showRefs: false,
          timestamp: new Date(),
        };
      });

      setChatHistory(history => {
        const updated = history
          .map(chat =>
            String(chat.id) === String(chatIdToUse)
              ? { ...chat, messages: next, timestamp: new Date() }
              : chat
          )
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        const current = updated.find(c => String(c.id) === String(chatIdToUse));

        if (current) {
          saveChatSession(
            chatIdToUse,
            current.title, 
            next,
            projectId,
            docsNow
          );
        } else {
          console.warn("No chat found to save title for", chatIdToUse);
        }

        return updated;
      });

      removePendingReq(projectId, chatIdToUse, botMsgId);
      return next;
    });
            
    
  
    if (result.referencedDocuments?.length > 0) {
      const docResponse = await fetch(`${API_URL}/chatbotDocumentDisplay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentIds: result.referencedDocuments,
          projectId,
          companyId
        }),
      });
      console.log('chatbotDocumentDisplay request payload:', {
        projectId,
        companyId,
      });
  
      const docData = await docResponse.json();
      if (docData?.documents) {
        setReferencedDocuments(docData.documents);
      }
    }
  };


  const canSend =
    inputValue.trim().length > 0 &&
    !(chatMode === 'gpt' ? gptIsSending : isProcessing) &&
    !hasProcessingUploads;


  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    if (hasProcessingUploads) {
      showUploadToast('Your file is still processing.');
      return;
    }

    if (chatMode === 'gpt') {
      if (gptIsSending) return;
      gptHandleSend();
    } else {
      if (isProcessing) return;
      handleSendMessage();
    }
  };


  const handleSendMessage = async (overridePrompt = null, overrideDocs = null) => {
    const latestDocs = await reloadFilteredDocuments();
    const validIds = new Set((latestDocs || []).map(d => d.id));

    if (activeChat) {
      setChatHistory(prev => prev.map(c => {
        if (String(c.id) !== String(activeChat)) return c;

        const cur = Array.isArray(c.selectedDocuments) ? c.selectedDocuments : [];
        const pruned = cur.filter(d => validIds.has(d.id));

        if (pruned.length !== cur.length) {
          saveChatSession(
            c.id,
            c.title,
            c.messages,
            projectId,
            pruned
          );
          setSelectedDocuments(pruned);
          setActiveDocuments(pruned);
          showUploadToast("Some selected documents were removed because they no longer exist in the project.");
        }

        return { ...c, selectedDocuments: pruned };
      }));
    }
    // 1) use the resolved prompt
    const prompt = (overridePrompt ?? inputValue) || '';

    // 2) use the resolved docs
    let docsNow = overrideDocs ?? selectedDocs;

    if (Array.isArray(docsNow) && Array.isArray(filteredDocuments)) {
      const validIds = new Set(filteredDocuments.map(d => d.id));
      const before = docsNow.length;
      docsNow = docsNow.filter(d => validIds.has(d.id));

      if (docsNow.length !== before) {
        pruneSelectedDocsForActiveChat(validIds, { silent: true });
        showUploadToast("Some selected documents were deleted/removed and won't be used.");
      }
    }
    const readyTempDocs = tempDocsUploading
      .filter(d => d.status === 'ready')
      .map(d => ({ tempDocId: d.tempDocId, filename: d.name }));

    const userId = auth?.user?.profile?.sub;

    const chatIdToUse = activeChat ? String(activeChat) : Date.now().toString();


    // 3) if no docs yet → stash prompt and open picker (no send)
    if ((!docsNow || docsNow.length === 0) && readyTempDocs.length === 0) {
      setPendingPrompt(prompt);
      setShowDocumentDialog(true);
      setIsProcessing(false);
      setProcessingSteps({ analyzing: false, filtering: false, preparing: false });
      if (!overridePrompt) setInputValue(''); 
      return;
    }

    phResearchQuerySent({ projectId, docCountInScope: docsNow.length });
    phTourResearchUsed();

    // 4) construct the user message with the prompt*
    const userMessage = {
      id: crypto.randomUUID(),
      text: prompt,
      isUser: true,
      timestamp: new Date()
    };

    if (!overridePrompt) setInputValue(''); 
    setInitialPrompt(prompt);
    setIsProcessing(true);
    setProcessingSteps({ analyzing: true, filtering: false, preparing: false });

    const botMsgId = crypto.randomUUID();
    queryStartTimesRef.current[botMsgId] = Date.now();
    const pendingBotMessage = {
      id: botMsgId,
      text: "Working on it…",
      progressMessage: resolveResearchProgressMessage({ status: 'pending', stage: 'QUEUED' }),
      researchStage: 'QUEUED',
      shortText: "",
      longText: "",
      hasDetailed: false,
      isDetailed: false,
      isUser: false,
      timestamp: new Date(),
      fromLLM: true,

      status: "pending",    
      jobId: null,          
      references: [],
      showRefs: false
    };

    // 5) create chat or append to existing
    if (!activeChat) {
      const newChatId = chatIdToUse;
      const newChatTitle = prompt.slice(0, 30) + (prompt.length > 30 ? '...' : '');
      const updatedMessages = [userMessage, pendingBotMessage];

      setMessages(updatedMessages);
      setActiveChat(newChatId);
      setChatHistory(prev => [...prev, {
        id: newChatId,
        title: newChatTitle,
        messages: updatedMessages,
        timestamp: new Date()
      }]);

      try {
        const res = await saveChatSession(
          newChatId,
          newChatTitle,
          updatedMessages,
          projectId,
          docsNow
        );
        console.log("chat title", res.newChatTitle);
        if (!res?.ok) throw new Error(`HTTP ${res?.status}: ${res?.statusText}`);
      } catch (e) {
        console.error('Error saving chat:', e);
      }
    } else {
      setMessages(prev => {
        const updated = [...prev, userMessage, pendingBotMessage];

        setChatHistory(history =>
          history
            .map(chat => {
              if (chat.id === activeChat) {
                const currentTitle = chat.title || 'New Chat';
                const updatedTitle =
                  currentTitle === 'New Chat'
                    ? prompt.slice(0, 30) + (prompt.length > 30 ? '...' : '')
                    : currentTitle;

                saveChatSession(
                  activeChat,
                  updatedTitle,
                  updated,
                  projectId,
                  docsNow
                );

                return {
                  ...chat,
                  title: updatedTitle,
                  messages: updated,
                  selectedDocuments: docsNow,
                  timestamp: new Date()
                };
              }
              return chat;
            })
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        );

        return updated;
      });
    }

    // 6) send directly with docsNow 
    try {

      const outgoingMessages = [...messages, userMessage, pendingBotMessage];
      upsertPendingReq(projectId, chatIdToUse, botMsgId, {
        prompt,
        docsNow,
        readyTempDocs,
        messages: outgoingMessages
        .filter(msg => !(msg.status === "pending")) 
        .map(msg => ({
          role: msg.isUser ? 'user' : 'assistant',
          text: msg.text
        })),
        context: {
          page: 'dashboard',
          projectId: projectId || null,
          userId: userId || null,
          selectedRequirement,
          requirementStatus: selectedRequirement?.status,
          companyId,
        },
      });
      const asyncResponse = await fetch(`${API_URL}/requirementsChatbotChat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId: chatIdToUse,
          messages: outgoingMessages
          .filter(msg => !(msg.status === "pending")) 
          .map(msg => ({
            role: msg.isUser ? 'user' : 'assistant',
            text: msg.text
          })),
          documents: docsNow,                
          tempDocuments: readyTempDocs,      
          context: {
            page: 'dashboard',
            projectId: projectId || null,
            userId: userId || null,
            selectedRequirement,
            requirementStatus: selectedRequirement?.status,
            companyId,
          }
        }),
      });


      if (!asyncResponse.ok)
        throw new Error(`Failed to enqueue chatbot job: ${asyncResponse.statusText}`);

      const { jobId } = await asyncResponse.json();
      upsertPendingReq(projectId, chatIdToUse, botMsgId, { jobId });
      setMessages(prev => {
        const next = prev.map(m => (m.id === botMsgId ? { ...m, jobId } : m));

        setChatHistory(history => {
          const updatedHistory = history.map(chat =>
            String(chat.id) === String(chatIdToUse)
              ? { ...chat, messages: next, timestamp: new Date() }
              : chat
          );

          const current = updatedHistory.find(c => String(c.id) === String(chatIdToUse));
          if (current) {
            saveChatSession(
              current.id,
              current.title,
              next,
              projectId,
              current.selectedDocuments || docsNow
            );
          }
          return updatedHistory;
        });

        return next;
      });
      await startPolling(jobId, botMsgId, chatIdToUse, docsNow);
    } catch (error) {
      console.error('Error:', error);
      markMessageFailed(
        chatIdToUse,
        botMsgId,
        docsNow,
        `Sorry, I couldn't send that request: ${error.message}. Please try again.`,
        'exception'
      );
    } finally {
      setIsProcessing(false);
      setProcessingSteps({ analyzing: false, filtering: false, preparing: false });
    }
  };

  const pollTempDocStatus = useCallback((tempDocId) => {
    let cancelled = false;

    async function tick() {
      const chatId = activeChat;
      const headers = buildAuthHeaders(auth);

      try {
        const res = await fetch(
          `${TEMP_UPLOAD_API_BASE}/chatbotTempUpload/status?chatId=${encodeURIComponent(chatId)}&tempDocId=${encodeURIComponent(tempDocId)}`,
          { headers }
        );
        const data = await res.json();

        if (data.status === 'READY') {
          if (!cancelled) {
            setTempDocsUploading(prev =>
              prev.map(doc =>
                doc.tempDocId === tempDocId
                  ? {
                      ...doc,
                      status: 'ready',
                      name: data.filename || doc.name
                    }
                  : doc
              )
            );
            showUploadToast(`"${data.filename || 'Your file'}" is ready. Ask your question.`);
          }
          return; 
        }

        if (data.status === 'FAILED') {
          if (!cancelled) {
            setTempDocsUploading(prev =>
              prev.map(doc =>
                doc.tempDocId === tempDocId
                  ? { ...doc, status: 'error' }
                  : doc
              )
            );
          }
          return; 
        }

        if (!cancelled) {
          setTimeout(tick, 2000);
        }
      } catch (err) {
        console.error('temp doc status poll error:', err);
        if (!cancelled) {
          setTempDocsUploading(prev =>
            prev.map(doc =>
              doc.tempDocId === tempDocId
                ? { ...doc, status: 'error' }
                : doc
            )
          );
        }
      }
    }

    tick();

    return () => { cancelled = true; };
  }, [TEMP_UPLOAD_API_BASE, activeChat, auth, setTempDocsUploading]);


  const handleDocumentToggle = (doc) => {
  setChatHistory(prev => prev.map(c => {
    if (String(c.id) !== String(activeChat)) return c;

    const exists = (c.selectedDocuments || []).some(d => d.id === doc.id);
    const updated = exists
      ? c.selectedDocuments.filter(d => d.id !== doc.id)
      : [...(c.selectedDocuments || []), doc];

    saveChatSession(c.id, c.title, c.messages, projectId, updated);

    setSelectedDocuments(updated);
    setActiveDocuments(updated);

    phDocumentSelected({
      projectId,
      docId: doc.id,
      docType: docType(doc),
      action: exists ? 'deselect' : 'select',
      docCountInScopeAfter: updated.length,
    });

    return { ...c, selectedDocuments: updated, timestamp: new Date() };
  }));
};


  const handleDocumentSelect = async (documents) => {
    const picked = Array.isArray(documents) ? documents : [documents];

    setSelectedDocuments(picked);
    setActiveDocuments(picked);
    setShowDocumentDialog(false);

    if (pendingPrompt) {
      const prompt = pendingPrompt;
      setPendingPrompt(null);
      await handleSendMessage(prompt, picked);
    }
  };



  const handleDialogClose = (proceed) => {
    setShowRequirementsDialog(false);
    if (proceed) {
      setAwaitingConfirmation(true);
      const selectedTitles = selectedDocs.map(doc => doc.title);
      setMessages(prev => [...prev, {
        text: `${selectedTitles.length > 1 
          ? t('dashboardChatbot.analyzeFollowingDocumentPlural', 'I\'ll analyze the following documents for you:')
          : t('dashboardChatbot.analyzeFollowingDocumentSingular', 'I\'ll analyze the following document for you:')
        }\n${selectedTitles.map(title => `- ${title}`).join('\n')}\n\n${t('dashboardChatbot.whatSpecificInfo', 'What specific information would you like to know about these documents?')}`,
        isUser: false,
        timestamp: new Date()
      }]);
    } else {
      const cancelMessage = {
        text: t('dashboardChatbot.requestCancelled', 'Request cancelled. Please try a different query or modify your search terms.'),
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, cancelMessage]);
      setPendingMessage(null);
      setFilteredRequirementsCount(0);
      setFilteredCards([]);
    }
    setSelectedDocuments([]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

   const handleNewChat = async () => {
    const welcomeMessage = {
      id: crypto.randomUUID(),
      text: t('dashboardChatbot.welcomeMessage', 'How can CODiii help you today? Select the documents you have questions for on the right and begin your research with me here'),
      isUser: false,
      timestamp: new Date()
    };

    const newChatId = crypto.randomUUID();
    const newChat = {
      id: newChatId,
      title: 'New Chat',
      messages: [welcomeMessage],
      timestamp: new Date()
    };

    setMessages([welcomeMessage]);
    setActiveChat(newChatId);
    setChatHistory(prev => [newChat, ...prev]);

    setTempDocsUploading([]);

    try {
      await saveChatSession(
        newChatId,
        'New Chat',
        [welcomeMessage],
        projectId,
        []
      );
    } catch (err) {
      console.error('Failed to save manually created chat session:', err);
    }

    setInputValue('');
    setSelectedDocuments([]);
    setActiveDocuments([]);
    setAwaitingConfirmation(false);
    setReferencedDocuments([]);
  };

  const loadChat = useCallback((chatId) => {
    const chat = chatHistory.find(c => String(c.id) === String(chatId));
    if (chat) {
      setMessages(structuredClone(chat.messages || []));
      setActiveChat(String(chatId));
      setInputValue('');
      setAwaitingConfirmation(false);
      setReferencedDocuments([]);

      const selectedDocs = Array.isArray(chat.selectedDocuments) ? chat.selectedDocuments : [];
      setSelectedDocuments(selectedDocs);
      setActiveDocuments(selectedDocs);
    }
  }, [chatHistory]);



  const handleDeleteChat = async (chatIdToDelete) => {
    try {
      const confirmed = window.confirm('Are you sure you want to delete this chat?');
      if (!confirmed) return;
  
      const res = await fetch(`${process.env.REACT_APP_CHAT_SESSION_API}/deleteChatSession`, {
        method: 'POST',
        headers: buildAuthHeaders(auth), 
        body: JSON.stringify({ chatId: chatIdToDelete }), 
      });
  
      if (!res.ok) throw new Error('Failed to delete chat session');
  
      setChatHistory(prev => prev.filter(chat => chat.id !== chatIdToDelete));
  
      if (String(chatIdToDelete) === String(activeChat)) {
        setActiveChat(null);
        setMessages([]);
      }
      } catch (err) {
      console.error('Error deleting chat session:', err);
    }
  };
  
  const getPromptFor = (idx) => {
    for (let i = idx - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.isUser && m.text) return m.text;
    }
    return initialPrompt || '(no prompt)';
  };

  
  const recognitionRef = useRef(null);

  const handleVoiceClick = () => {
    if (!isRecording) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert("Your browser does not support speech recognition.");
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.interimResults = true;  
      recognition.continuous = true;     

      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = 0; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        setInputValue(finalTranscript + interimTranscript);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognition.start();
      recognitionRef.current = recognition;
      setIsRecording(true);
    } else {
      recognitionRef.current?.stop();
      setIsRecording(false);
    }
  };

  const ensureActiveChatId = async () => {
    if (activeChat) return String(activeChat);

    const welcomeMessage = {
      id: crypto.randomUUID(),
      text: t(
        'dashboardChatbot.welcomeMessage',
        'How can CODiii help you today? Select the documents you have questions for on the right and begin your research with me here'
      ),
      isUser: false,
      timestamp: new Date()
    };

    const newChatId = crypto.randomUUID();

    setMessages([welcomeMessage]);
    setActiveChat(newChatId);
    setChatHistory(prev => [{
      id: newChatId,
      title: 'New Chat',
      messages: [welcomeMessage],
      selectedDocuments: [],
      timestamp: new Date()
    }, ...prev]);

    try {
      await saveChatSession(
        newChatId,
        'New Chat',
        [welcomeMessage],
        projectId,
        []
      );
    } catch (err) {
      console.error('Failed to save initial chat session (ensureActiveChatId):', err);
    }

    return newChatId;
  };

  const handleAttachClick = async () => {
    if (!projectId) {
      alert('Select a project first to attach a document to this chat.');
      return;
    }

    await ensureActiveChatId();       
    if (fileInputRef.current) fileInputRef.current.click();
  };

    const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setTempFile({
      file,
      name: file.name,
      type: file.type,
      size: file.size,
    });

    try {
      const chatId = await ensureActiveChatId(); 
      const userId = auth?.user?.profile?.sub;
      const projId = projectId;

      if (!chatId || !userId || !projId) {
        console.warn("Missing required init-upload fields", { chatId, userId, projId });
        alert("Missing chat, user, or project context for upload.");
        return;
      }

      const mimeToUse =
        file.type && file.type.trim() !== ""
          ? file.type
          : "application/octet-stream";

      const initBody = {
        chatId,
        projectId: projId,
        fileName: file.name,
        mimeType: mimeToUse,
        sizeBytes: file.size
      };

      const headers = {
        ...buildAuthHeaders(auth),
        "Content-Type": "application/json",
      };

      const initRes = await fetch(
        `${TEMP_UPLOAD_API_BASE}/chatbotTempUpload/init`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(initBody),
        }
      );

      const initData = await initRes.json();
      setTempUploadInitResult({
        status: initRes.status,
        data: initData,
      });

      if (!initRes.ok) {
        alert(
          `Init upload failed: ${initRes.status} ${
            initData.error || initData.message || ""
          }`
        );
        return;
      }


      const { tempDocId, uploadUrl, requiredHeaders } = initData;

      setTempDocsUploading(prev => [
        ...prev,
        {
          tempDocId,
          name: file.name,
          status: 'uploading',
          progress: 0
        }
      ]);

      const putHeaders = {
        ...requiredHeaders,
      };

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (e) => {
          if (!e.lengthComputable) return;
          const pct = Math.max(0, Math.min(100, Math.round((e.loaded / e.total) * 100)));
          patchTempUpload(tempDocId, { progress: pct, status: 'uploading' });
        };

        xhr.onload = () => {
          resolve();
        };

        xhr.onerror = () => {
          patchTempUpload(tempDocId, { status: 'error' });
          reject(new Error('Upload failed'));
        };

        xhr.open("PUT", uploadUrl, true);
        Object.entries(putHeaders || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        xhr.send(file);
      });

      patchTempUpload(tempDocId, { status: 'processing' });

      const commitRes = await fetch(
        `${TEMP_UPLOAD_API_BASE}/chatbotTempUpload/commit`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            chatId,
            tempDocId
          })
        }
      );
      const commitData = await commitRes.json();

      if (!commitRes.ok) {
        alert("Upload commit failed.");
        setTempDocsUploading(prev =>
          prev.map(doc =>
            doc.tempDocId === tempDocId
              ? { ...doc, status: 'error' }
              : doc
          )
        );
        return;
      }
      pollTempDocStatus(tempDocId);

      alert("Upload succeeded: file is queued for analysis.");
    } catch (err) {
      console.error("Error in upload flow:", err);
      setTempUploadInitResult({
        status: "NETWORK_ERROR",
        data: { error: err.message },
      });
      alert("Unexpected upload error, see console.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };


  const handleFilterToggle = (categoryId) => {
    setActiveFilters(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };
  
  const saveChatSession = async (chatId, chatTitle, messages, projectId, docs = selectedDocs) => {
    if (!chatId || !projectId) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_CHAT_SESSION_API}/chatSession`, {
        method: 'POST',
        headers: buildAuthHeaders(auth), 
        body: JSON.stringify({
          chatId,
          title: chatTitle,
          messages: Array.isArray(messages) ? messages : [],
          projectId,
          selectedDocuments: docs || [],
          timestamp: new Date().toISOString(),
        }),
      });

      if (!res.ok) {
        const errorBody = await res.text();
        throw new Error(`Save failed with status ${res.status}: ${errorBody}`);
      }
      return res;
    } catch (error) {
      console.error('Failed to save chat session:', error);
    }
  };

  
  const toggleMessageDetail = useCallback((msgIndex) => {
    setMessages(prevMsgs => {
      const next = [...prevMsgs];
      const m = next[msgIndex];
      if (!m || !m.hasDetailed) return prevMsgs;

      const nowDetailed = !m.isDetailed;
      next[msgIndex] = {
        ...m,
        isDetailed: nowDetailed,
        text: nowDetailed ? (m.longText || m.text) : (m.shortText || m.text),
      };

      setChatHistory(history => {
        const updated = history.map(chat =>
          String(chat.id) === String(activeChat)
            ? { ...chat, messages: next, timestamp: new Date() }
            : chat
        );
        const current = updated.find(c => String(c.id) === String(activeChat));
        if (current) {
          saveChatSession(
            current.id,
            current.title,
            next,
            projectId,
            current.selectedDocuments || []
          );
        }
        return updated;
      });

      return next;
    });
  }, [activeChat, auth?.user?.profile?.sub, projectId, saveChatSession]);

  const persistThumb = useCallback((msgIndex, value) => {
    phResearchThumb({ projectId, messageIndex: msgIndex, value });
    setMessages(prevMsgs => {
      const next = prevMsgs.map((m, i) => (i === msgIndex ? { ...m, _thumbs: value } : m));

      setChatHistory(history => {
        const updated = history.map(chat =>
          String(chat.id) === String(activeChat)
            ? { ...chat, messages: next, timestamp: new Date() }
            : chat
        );
        const current = updated.find(c => String(c.id) === String(activeChat));
        if (current) {
          saveChatSession(
            current.id,
            current.title,
            next,
            projectId,
            current.selectedDocuments || []
          );
        }
        return updated;
      });

      return next;
    });
  }, [activeChat, auth?.user?.profile?.sub, projectId, saveChatSession]);

  
  const openPdfReference = async (ref, docMeta) => {

  try {
    if (!ref?.originalPdfKey) {
      console.warn('No originalPdfKey for reference:', ref);
      alert('Sorry, I cannot open this reference (missing PDF key).');
      return;
    }

    if (!projectId) {
      console.warn('No projectId in DashboardChatbot props');
      alert('No project selected for this reference.');
      return;
    }

    if (!CODES_API_BASE) {
      console.error('REACT_APP_CODES_API_BASE is not configured.');
      alert('Codes API is not configured on the frontend (missing REACT_APP_CODES_API_BASE).');
      return;
    }

    const fileId = docMeta?.id || ref.fileId || ref.originalPdfKey;

    const refType = ref.type || null; 
    const refKey  = ref.value || ref.refKey || null;  

    if (!refKey) {
      console.warn('Reference missing value/refKey:', ref);
      alert('This reference is missing an identifier.');
      return;
    }

    const headers = {
      ...buildAuthHeaders(auth),   
    };

    const qs = new URLSearchParams({
      projectId: String(projectId),
      fileId: String(fileId),
      originalPdfKey: ref.originalPdfKey,
      refKey: refKey,
    });

    if (refType) qs.append('refType', refType);

    const url = `${CODES_API_BASE}/codes/resolve?${qs.toString()}`;

    const res = await fetch(url, {
      method: 'GET',
      headers,
    });

    const rawBody = await res.text();

    let data;
    try {
      data = rawBody ? JSON.parse(rawBody) : {};
    } catch (parseErr) {
      console.warn('Failed to parse JSON from codes/resolve, using raw body:', parseErr);
      data = { raw: rawBody };
    }

    if (!res.ok) {
      console.error('codes/resolve failed', res.status, data);
      const msg = data?.error || `codes/resolve returned ${res.status}`;
      alert(`Could not resolve reference: ${msg}`);
      return;
    }

    const pdfUrl =
      data.presignedPdfUrl ||
      data.pdfUrl ||
      data.url;

    if (!pdfUrl) {
      console.error('codes/resolve: no pdfUrl/presignedPdfUrl/url in response', data);
      alert('Could not open PDF (missing URL in resolve response).');
      return;
    }

    const page =
      typeof data.definitionPage === 'number' && data.definitionPage > 0
        ? data.definitionPage
        : (typeof ref.pageNumber === 'number' && ref.pageNumber > 0
            ? ref.pageNumber
            : 1);

    const highlightText = data.preview || refKey;

    setPdfViewer({
      open: true,
      url: pdfUrl,
      initialPage: page,
      title: docMeta?.title || ref.displayName || ref.fileName || 'PDF Document',
      highlightText,
    });
  } catch (err) {
    console.error('Error in openPdfReference:', err);
    alert(`Unexpected error opening PDF: ${err.message}`);
  }
};

const openPdfRefPage = async (ref, docMeta, page) => {
  try {
    if (!PDF_SIGNED_URL_API) {
      console.error('PDF_SIGNED_URL_API not configured');
      alert('PDF signed-url API is not configured on the frontend.');
      return;
    }

    const originalPdfKey = ref.originalPdfKey || ref.key;
    if (!originalPdfKey) {
      console.warn('No originalPdfKey on ref:', ref);
      alert('Sorry, I cannot open this page (no PDF key found).');
      return;
    }

    const res = await fetch(PDF_SIGNED_URL_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ originalPdfKey }),
    });

    const bodyText = await res.text();
    let data;
    try {
      data = bodyText ? JSON.parse(bodyText) : {};
    } catch (e) {
      console.warn('Failed to parse JSON from chatbotPdfSignedUrl:', e);
      data = {};
    }

    if (!res.ok) {
      console.error('chatbotPdfSignedUrl failed', res.status, data);
      const msg = data?.error || `chatbotPdfSignedUrl returned ${res.status}`;
      alert(`Could not open reference page: ${msg}`);
      return;
    }

    const pdfUrl = data.url || data.presignedPdfUrl || data.pdfUrl;
    if (!pdfUrl) {
      console.error('chatbotPdfSignedUrl: no url in response', data);
      alert('Could not open PDF (missing URL from signed-url API).');
      return;
    }

    const pageNumber =
      typeof page === 'number' && page > 0
        ? page
        : (Array.isArray(ref.pages) && ref.pages[0]) || 1;

    setPdfViewer({
      open: true,
      url: pdfUrl,
      initialPage: pageNumber,
      title: docMeta?.title || `${ref.type || 'Reference'} ${ref.value || ''}`,
      highlightText: docMeta?.snippet || ref.value || ref.label || null,
    });
  } catch (err) {
    console.error('Error in openPdfRefPage:', err);
    alert(`Unexpected error opening reference page: ${err.message}`);
  }
};


    const openPdfPageFromDoc = async (docMeta) => {

  try {
    if (!PDF_SIGNED_URL_API) {
      console.error('PDF_SIGNED_URL_API not configured');
      alert('PDF signed-url API is not configured on the frontend.');
      return;
    }

    const firstRef = Array.isArray(docMeta.references)
      ? docMeta.references.find(r => r.originalPdfKey || r.key)
      : null;

    const originalPdfKey = firstRef?.originalPdfKey || firstRef?.key;
    if (!originalPdfKey) {
      console.warn('No originalPdfKey on docMeta.references:', docMeta);
      alert('Sorry, I cannot open this snippet (no PDF key found).');
      return;
    }

    const res = await fetch(PDF_SIGNED_URL_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ originalPdfKey }),
    });

    const bodyText = await res.text();
    let data;
    try {
      data = bodyText ? JSON.parse(bodyText) : {};
    } catch (e) {
      console.warn('Failed to parse JSON from chatbotPdfSignedUrl:', e);
      data = {};
    }

    if (!res.ok) {
      console.error('chatbotPdfSignedUrl failed', res.status, data);
      const msg = data?.error || `chatbotPdfSignedUrl returned ${res.status}`;
      alert(`Could not open snippet page: ${msg}`);
      return;
    }

    const pdfUrl = data.url || data.presignedPdfUrl || data.pdfUrl;
    if (!pdfUrl) {
      console.error('chatbotPdfSignedUrl: no url/presignedPdfUrl in response', data);
      alert('Could not open PDF (missing URL from signed-url API).');
      return;
    }

    const page =
      typeof docMeta.pageNumber === 'number' && docMeta.pageNumber > 0
        ? docMeta.pageNumber
        : 1;

    setPdfViewer({
      open: true,
      url: pdfUrl,
      initialPage: page,
      title: docMeta.title || 'PDF Document',
      highlightText: docMeta.snippet || docMeta.highlightText || null,
    });
  } catch (err) {
    console.error('Error in openPdfPageFromDoc:', err);
    alert(`Unexpected error opening snippet page: ${err.message}`);
  }
};




  
  const loadChatSessions = async (projectId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_CHAT_SESSION_API}/chatSessions?projectId=${projectId}`,
        { headers: buildAuthHeaders(auth) } 
      );

      const data = await res.json();
      return data || [];
    } catch (err) {
      console.error("Error loading chat sessions:", err);
      return [];
    }
  };

  const gptNewChat = () => {
    setGptActiveId(null);
    setGptMessages([]); 
  };

  const gptLoadChat = async (conversationId) => {
    try {
      setGptActiveId(conversationId);
      const hist = await gptGetHistory(conversationId, auth);
      setGptMessages(
        hist.map(m => ({
          text: m.content,
          isUser: m.role === 'user',
          timestamp: new Date(m.createdAt),
          fromLLM: m.role === 'assistant',
          fromGPT: true,
        }))
      );
    } catch (e) {
      console.error('GPT history load failed:', e);
    }
  };

  const gptHandleSend = async () => {
    const prompt = (inputValue || '').trim();
    if (!prompt) return;
    setInputValue('');

    const userMsg = { text: prompt, isUser: true, timestamp: new Date() };
    setGptMessages(prev => [...prev, userMsg]);
    setGptIsSending(true);

    try {
      const { conversationId, assistantText } = await gptSendMessage(
        { conversationId: gptActiveId, userText: prompt },
        auth
      );
      if (!gptActiveId) setGptActiveId(conversationId);

      const botMsg = {
        text: assistantText,
        isUser: false,
        timestamp: new Date(),
        fromLLM: true,
        fromGPT: true,
      };
      setGptMessages(prev => [...prev, botMsg]);

      const list = await gptListConversations(auth);
      setGptConversations(list);
    } catch (e) {
      console.error('GPT send failed:', e);
      setGptMessages(prev => [
        ...prev,
        { text: `Error: ${e.message}`, isUser: false, timestamp: new Date() },
      ]);
    } finally {
      setGptIsSending(false);
    }
  };

  const handleGptDelete = async (conversationId) => {
    const ok = window.confirm('Delete this GPT conversation?');
    if (!ok) return;

    try {
      await gptDeleteConversation(conversationId, auth);

      setGptConversations(prev => prev.filter(c => c.conversationId !== conversationId));

      if (String(conversationId) === String(gptActiveId)) {
        setGptActiveId(null);
        setGptMessages([]);
      }
    } catch (e) {
      console.error('GPT delete failed:', e);
      alert('Failed to delete conversation.');
    }
  };

  const visibleMessages = chatMode === 'gpt' ? gptMessages : messages;
  const showingWelcome = chatMode === 'gpt' ? false : showWelcomeLayout;
  const isWeb = chatMode === 'gpt';
  const webTooltip = isWeb ? 'Back to Project' : 'Ask the Web';
  return (
    <ChatbotContainer className="dashboard-chatbot">
      <ChatContainer>
        <SideShell $side="left"
  $collapsed={leftCollapsed}
  $expandedWidth="clamp(260px, 28vw, 400px)"
  $railWidth={50}
>
  <SideInner $collapsed={leftCollapsed} $direction="left">
    {chatMode === 'gpt' ? (
      <Sidebar>
    <NewChatBtn onClick={gptNewChat}>New GPT Chat</NewChatBtn>

    <SearchConversations>
      <SearchIcon />
      <SearchConversationsInput
        placeholder="Search GPT conversations..."
        value={gptSearch}
        onChange={(e) => setGptSearch(e.target.value)}
      />
      <SearchGlyph />
    </SearchConversations>

    <ConversationList>
      {gptConversations
        .filter(c =>
          (c.title || 'Chat').toLowerCase().includes(gptSearch.toLowerCase()) ||
          (c.lastPreview || '').toLowerCase().includes(gptSearch.toLowerCase())
        )
        .map(c => (
          <ConversationItem
            key={c.conversationId}
            className={String(c.conversationId) === String(gptActiveId) ? 'active' : ''}
            onClick={() => gptLoadChat(c.conversationId)}
          >
            <ConversationHeader>
              <ConversationTitle>{c.title || 'Chat'}</ConversationTitle>
              <ConversationDate>
                {c.updatedAt ? new Date(c.updatedAt).toLocaleDateString() : ''}
              </ConversationDate>
            </ConversationHeader>
            <ConversationPreview>
              {(c.lastPreview || '').replace(/\s+/g, ' ').trim()}
            </ConversationPreview>
            <DeleteBtn
              onClick={(e) => {
                e.stopPropagation();
                handleGptDelete(c.conversationId);
              }}
              title="Delete GPT chat"
            >
              <FiTrash2 />
            </DeleteBtn>
          </ConversationItem>
        ))}
        {gptConversations.length === 0 && (
          <div style={{ color:'#777', fontSize:12, padding:'8px 16px' }}>
            No GPT conversations yet.
          </div>
        )}
    </ConversationList>
  </Sidebar>
 ) : (
  <Sidebar>

    <NewChatBtn onClick={handleNewChat}>

    {t('dashboardChatbot.newChat', 'New Chat')}
  </NewChatBtn>

  <SearchConversations>
    <SearchIcon />
    <SearchConversationsInput
      placeholder={t('dashboardChatbot.searchConversations', 'Search conversations...')}
      value={historySearch}
      onChange={(e) => setHistorySearch(e.target.value)}
    />
    <SearchGlyph />
  </SearchConversations>

  <ConversationList>
    {chatHistory
      .filter(c =>
        (c.title || 'New Chat').toLowerCase().includes(historySearch.toLowerCase())
        || (c.messages?.some(m => (m.text || '').toLowerCase().includes(historySearch.toLowerCase())))
      )
      .map(chat => (
        <ConversationItem
          key={chat.id}
          className={String(chat.id) === String(activeChat) ? 'active' : ''}
          onClick={() => loadChat(chat.id)}
          onMouseEnter={(e) => showTooltip(e, chat)}
          onMouseMove={moveTooltip}
          onMouseLeave={hideTooltip}
        >
          <ConversationHeader>
            <ConversationTitle>{chat.title || 'New Chat'}</ConversationTitle>
            <ConversationDate>
              {new Date(chat.timestamp || Date.now()).toLocaleDateString()}
            </ConversationDate>
          </ConversationHeader>

          <ConversationPreview>
            {(chat.messages?.slice(-1)[0]?.text || '').replace(/\s+/g, ' ').trim()}
          </ConversationPreview>

          <DeleteBtn
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteChat(chat.id);
            }}
            title={t('dashboardChatbot.deleteChat', 'Delete chat')}
          >
            <FiTrash2 />
          </DeleteBtn>
        </ConversationItem>
      ))}
  </ConversationList>

  <ConversationTooltip
    className={tooltip.show ? 'show' : ''}
    style={{ left: tooltip.x, top: tooltip.y }}
  >
    <TooltipTitle>{tooltip.title}</TooltipTitle>
    <TooltipContent>{tooltip.preview}</TooltipContent>
    <TooltipMeta>
      <span>{tooltip.metaLeft}</span>
      <span>{tooltip.metaRight}</span>
    </TooltipMeta>
  </ConversationTooltip>
</Sidebar>
)}
  </SideInner>

  <SideRail
    $collapsed={leftCollapsed}
    $railWidth={50}
    type="button"
    onClick={() => setLeftCollapsed(false)}
    aria-label="Expand chat panel"
    aria-expanded={!leftCollapsed}
  >
    <RailIcon aria-hidden="true">
      <FiMessageSquare size={20} />
    </RailIcon>
  </SideRail>

  <EdgeGripZone
    $side="left"
    $collapsed={leftCollapsed}
    type="button"
    onClick={toggleLeft}
    aria-label="Collapse chat panel"
    title="Collapse chat panel"
  >
    <EdgeGrip aria-hidden="true" />
  </EdgeGripZone>

</SideShell>
  <CenterWrap>
        <ChatWindow>

        <ChatBody>
          <ChatBodyInner>
          {showingWelcome ? (
            <FancyWelcome
              inputValue={inputValue}
              setInputValue={setInputValue}
              onSend={chatMode === 'gpt' ? gptHandleSend : handleSendMessage}
              onAttach={handleAttachClick}
              tempDocsUploading={chatMode === 'gpt' ? [] : tempDocsUploading}
              onWebToggle={() => setChatMode(m => (m === 'gpt' ? 'project' : 'gpt'))}
              isWeb={isWeb}
              webTooltip={webTooltip}
              onVoiceClick={handleVoiceClick}
              isRecording={isRecording}
              disableSend={hasProcessingUploads}
              showUploadToast={showUploadToast}
              canSend={canSend}
              hasProcessingUploads={hasProcessingUploads}
              sandboxMode={sandboxMode}
            />
          ) : (
            <>
              {visibleMessages.map((message, index) => {
                const thumb = message._thumbs;
                return (
                  <Message key={message.id || `${activeChat}-${index}`} $isUser={message.isUser}>
                    {!message.isUser && (
                      <BotIconWrapper>
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
                        </svg>
                      </BotIconWrapper>
                    )}

                    <MessageBubble $isUser={message.isUser} $thumb={thumb} $pending={message.status === 'pending'}>
                      {message.status === 'pending' && !message.isUser ? (
                        <ResearchProgressIndicator
                          isProcessing
                          progressMessage={message.progressMessage || 'Analyzing your question…'}
                        />
                      ) : (
                        <MarkdownWrapper $isUser={message.isUser}>
                          <ReactMarkdown>{message.text}</ReactMarkdown>
                        </MarkdownWrapper>
                      )}
                      {chatMode !== 'gpt' && !message.isUser && hasRefs(message) && (
                        <DocsPillsRow>
                        {message.references.map((r, i) => {
                          const primaryRef = Array.isArray(r.references)
                            ? r.references.find(ref => ref.originalPdfKey)
                            : null;

                          const clickable = !!primaryRef;

                          return (
                            <DocPill
                              key={r.id || i}
                              $clickable={clickable}
                              onClick={
                                clickable
                                  ? () => {
                                      openPdfPageFromDoc(r);
                                      phCitationClicked({
                                        projectId,
                                        source: 'chatbot',
                                        docId: r.id,
                                        pageNumber: r.pageNumber ?? primaryRef?.pageNumber ?? null,
                                      });
                                    }
                                  : undefined
                              }

                                  title={r.title || 'Document'}
                                >
                              <FiFileText size={12} />
                              <span className="title">
                                {r.title || 'Document'}
                              </span>
                              {typeof r.pageNumber === 'number' && (
                                <span className="page">p. {r.pageNumber}</span>
                              )}
                            </DocPill>
                          );
                        })}

                        </DocsPillsRow>
                      )}

                      {chatMode !== 'gpt' && !message.isUser && message.fromLLM && message.status !== 'pending' && (
                        <FeedbackBar>
                          {message.hasDetailed && (
                            <ToggleDetailsBtn
                              className={message.isDetailed ? 'active' : ''}
                              title={message.isDetailed ? 'View concise answer' : 'View detailed answer'}
                              onClick={() => toggleMessageDetail(index)}
                            >
                              <FiFileText size={16} />
                              {message.isDetailed ? 'Concise' : 'Detailed'}
                            </ToggleDetailsBtn>
                          )}
                          <button
                            className={`up ${thumb === 'up' ? 'active' : ''} ${thumb && thumb !== 'up' ? 'inactive' : ''}`}
                            title={t('dashboardChatbot.goodAnswer', 'Good answer')}
                            onClick={() => persistThumb(index, 'up')}
                            disabled={Boolean(thumb)}
                          >
                            <FiThumbsUp size={16} />
                          </button>
                          <button
                            className={`down ${thumb === 'down' ? 'active' : ''} ${thumb && thumb !== 'down' ? 'inactive' : ''}`}
                            title={t('dashboardChatbot.badAnswer', 'Bad answer')}
                            onClick={async () => {
                              persistThumb(index, 'down');
                              try {
                                const userId = auth?.user?.profile?.sub;
                                const project = projectId;
                                const chatIdLocal = activeChat;
                                await fetch(process.env.REACT_APP_FEEDBACK_API, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    userId,
                                    projectId: project,
                                    chatId: String(chatIdLocal),
                                    messageId: message.id ?? index,
                                    prompt: getPromptFor(index),
                                    response: message.longText || message.text || '',
                                    model: 'gpt-4.1',
                                    referencedDocuments: referencedDocuments?.map(d => d.title) || [],
                                    createdAt: new Date().toISOString()
                                  })
                                });
                              } catch (e) {
                                console.error('feedback failed', e);
                              }
                            }}
                            disabled={Boolean(thumb)}
                          >
                            <FiThumbsDown size={16} />
                          </button>
                        </FeedbackBar>
                      )}
                      {chatMode !== 'gpt' && !message.isUser && hasRefs(message) && (
                        <div style={{ marginTop: 6, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <ToggleDetailsBtn
                            className={message.showRefs ? 'active' : ''}
                            onClick={() => {
                              const msgId = message.id;

                              setMessages(prev => {
                                const copy = prev.map(m =>
                                  (msgId && m.id === msgId)
                                    ? { ...m, showRefs: !m.showRefs }
                                    : m
                                );
                                if (!msgId && copy[index]) {
                                  copy[index] = { ...copy[index], showRefs: !copy[index].showRefs };
                                }

                                setChatHistory(h =>
                                  h.map(chat =>
                                    String(chat.id) === String(activeChat)
                                      ? { ...chat, messages: copy, timestamp: new Date() }
                                      : chat
                                  )
                                );

                                const currentTitle =
                                  chatHistory.find(c => String(c.id) === String(activeChat))?.title;

                                saveChatSession(
                                  activeChat,
                                  currentTitle,
                                  copy,
                                  projectId,
                                  selectedDocs
                                );

                                return copy;
                              });
                            }}
                          >
                            References ({message.references.length})
                          </ToggleDetailsBtn>
                        </div>
                      )}

                      {/* References panel */}
                      {chatMode !== 'gpt' && !message.isUser && message.showRefs && hasRefs(message) && (
                        <ReferencesWrap>
                          <RefHeader>
                            <span>Where this answer comes from</span>
                            <span style={{ color: '#6b7280', fontSize: 12 }}>
                              {message.references.length} source{message.references.length > 1 ? 's' : ''}
                            </span>
                          </RefHeader>
                          <RefList>
                            {message.references.map((r, rIdx) => (
                              <RefItem key={r.id || rIdx}>
                                <RefTitle>
                                  <FiFileText />
                                  <span>{r.title || 'Document'}</span>
                                </RefTitle>
                                {(typeof r.pageNumber === 'number' || typeof r.score === 'number') && (
                                  <div style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 4px' }}>
                                    {typeof r.pageNumber === 'number' && <>Page {r.pageNumber}</>}
                                    {typeof r.pageNumber === 'number' && typeof r.score === 'number' && ' • '}
                                    {typeof r.score === 'number' && <>score {r.score.toFixed(2)}</>}
                                  </div>
                                )}
                                <RefSnippet>
                                  {r.snippet?.trim() ? r.snippet : '(No snippet available)'}
                                </RefSnippet>
                                {Array.isArray(r.references) && r.references.length > 0 && (
                                  <div style={{ marginTop: 8 }}>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                                      Found references ({r.references.length})
                                    </div>

                                    <PillsRow>
                                      {r.references.map((ref, i) => {
                                        const full = `${ref.type}: ${ref.value ?? ''}`.trim();

                                        const clickable = !!ref.originalPdfKey; 

                                        return (
                                          <ReferencePill
                                            key={i}
                                            title={full}
                                            $clickable={clickable}
                                            onClick={
                                                clickable
                                                  ? () => {
                                                      let page = ref.pageNumber || r.pageNumber || null;
                                                      if (ref.type === "Page") {
                                                        page =
                                                          ref.pageNumber ||
                                                          Number(ref.value?.toString().replace(/\D/g, "")) ||
                                                          r.pageNumber ||
                                                          1;

                                                        openPdfRefPage(ref, r, page);
                                                      } else {
                                                        openPdfReference(ref, r);
                                                      }

                                                      phCitationClicked({
                                                        projectId,
                                                        source: 'chatbot',
                                                        docId: r.id,
                                                        pageNumber: page,
                                                      });
                                                    }
                                                  : undefined
                                              }
                                            >
                                            <strong>{ref.type}:</strong>{' '}
                                            {ref.value}
                                          </ReferencePill>
                                        );
                                      })}
                                    </PillsRow>
                                  </div>
                                )}
                              </RefItem>
                            ))}
                          </RefList>
                        </ReferencesWrap>
                      )}
                    </MessageBubble>
                  </Message>
                );
              })}

              {((chatMode === 'gpt' ? gptIsSending : isProcessing) &&
                !visibleMessages.some(m => m.status === 'pending' && !m.isUser)) && <Spinner />}
              <div ref={messagesEndRef} />
            </>
          )}
          </ChatBodyInner>
        </ChatBody>


            {!showingWelcome && (
        <>
          <ChatInputWrapper>
            <ChatInputForm onSubmit={handleSubmit}>
              <ChatInputBar>
                <Input
                  type="text"
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  placeholder={t('dashboardChatbot.typeMessage', 'Type your message...')}
                />

                {!sandboxMode && chatMode !== 'gpt' && (
                <div style={{ position: 'relative' }}>
                  <AttachButton
                    type="button"
                    onClick={handleAttachClick}
                    title="Attach a file for this chat"
                    aria-label="Attach a file for this chat"
                    data-title="Attach a file"
                  >
                    <FiPaperclip size={18} />
                  </AttachButton>

                  {tempDocsUploading.some(d => d.status === 'processing' || d.status === 'uploading') && (
                    <span
                      aria-label="Uploads in progress"
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        minWidth: 18,
                        height: 18,
                        padding: '0 4px',
                        borderRadius: 999,
                        background: '#f59e0b',
                        color: 'white',
                        fontSize: 11,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid white',
                        boxShadow: '0 1px 6px rgba(0,0,0,.18)'
                      }}
                    >
                      {tempDocsUploading.filter(d => d.status === 'processing').length}
                    </span>
                  )}
                </div>
              )}

                {!sandboxMode && (
                  <WebModeButton
                    type="button"
                    $active={isWeb}
                    data-title={webTooltip}
                    aria-label={webTooltip}
                    title={webTooltip}
                    aria-pressed={isWeb}
                    onClick={() => setChatMode(m => (m === 'gpt' ? 'project' : 'gpt'))}
                  >
                    {isWeb ? <FiFileText size={18} /> : <FiGlobe size={18} />}
                  </WebModeButton>
                )}
                <VoiceButton
                  type="button"
                  onClick={handleVoiceClick}
                  isRecording={isRecording}
                >
                  <i className="fas fa-microphone" />
                </VoiceButton>

                <SendButton
                  type="submit"
                  disabled={!canSend}
                  title={
                    hasProcessingUploads
                      ? 'Disabled while your file is processing'
                      : (!inputValue.trim() ? 'Type a message to enable Send' : undefined)
                  }
                >
                  {t('dashboardChatbot.send', 'Send')}
                </SendButton>

              </ChatInputBar>
            </ChatInputForm>
          </ChatInputWrapper>

          {chatMode !== 'gpt' && tempDocsUploading.length > 0 && (
            <div
              style={{
                padding: '8px 20px 0',
                fontSize: '12px',
                lineHeight: 1.4,
                color: '#444',
              }}
            >
              <div
                style={{
                  marginBottom: 4,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '.04em',
                  color: '#6b7280',
                  fontWeight: 600,
                }}
              >
                Temporary files for this chat
              </div>

              <div style={{ paddingRight: 20 }}>
                <TempUploadChips items={tempDocsUploading} />
              </div>
            </div>
          )}
        </>
      )}
        </ChatWindow>
        </CenterWrap>
        {chatMode !== 'gpt' && (
          <SideShell $side="right"
            $collapsed={rightCollapsed}
            $expandedWidth="clamp(300px, 28vw, 450px)"
            $railWidth={50}
          >
            <EdgeGripZone
              $side="right"
              $collapsed={rightCollapsed}
              type="button"
              onClick={toggleRight}
              aria-label="Collapse documents panel"
              title="Collapse documents panel"
            >
              <EdgeGrip aria-hidden="true" />
            </EdgeGripZone>

            <SideInner $collapsed={rightCollapsed} $direction="right">
              <DocumentsPanel data-tour="research-doc-panel">
                <DocumentsHeaderX>

          <h2>{t('dashboardChatbot.selectDocuments', 'Select Documents')}</h2>
          <p>{t('dashboardChatbot.chooseFilesToSearch', 'Choose which files to search')}</p>
          {projectId && sidebarLoading && (

            <LoadingStrip aria-label="Loading documents" role="progressbar">
              <LoadingThumb />
            </LoadingStrip>
          )}
        </DocumentsHeaderX>

        <SearchFilterRow>
          <SearchBox>
            <DocsSearchInput
              placeholder={t('dashboardChatbot.searchDocuments', 'Search documents...')}
              value={sidePanelSearch}
              onChange={(e) => setSidePanelSearch(e.target.value)}
            />
          </SearchBox>
          <FilterSelect
            value={(activeFilters && activeFilters[0]) || 'all'}
            onChange={(e) => setActiveFilters(e.target.value === 'all' ? [] : [e.target.value])}
          >
            <option value="all">{t('dashboardChatbot.allTypes', 'All Types')}</option>
            <option value="architectural">Architectural</option>
            <option value="structural">Structural</option>
            <option value="mechanical">Mechanical</option>
            <option value="contract">Contract</option>
            <option value="client">Client</option>
          </FilterSelect>
        </SearchFilterRow>

        <DocumentsScroll>
          {!projectId ? (
            <div style={{ padding: 24, color: '#666', fontSize: 14 }}>
              {t('dashboardChatbot.selectAProjectToSeeDocs', 'Select a project to see its documents here.')}
            </div>
          ) : sidebarLoading ? (
            <div aria-live="polite" aria-busy="true">
              <DocSection>
                <SectionHeaderRow>
                  <SectionTitle>
                    <SectionIndicator />
                    {t('dashboardChatbot.availableFiles', 'Available Files')}
                  </SectionTitle>
                </SectionHeaderRow>
                {[...Array(6)].map((_, i) => (
                  <SkeletonItem key={i}>
                    <SkeletonIcon />
                    <SkeletonText>
                      <div className="line1" />
                      <div className="line2" />
                    </SkeletonText>
                  </SkeletonItem>
                ))}
              </DocSection>
            </div>
          ) : (
            <>
          {/* Selected Files */}
          <DocSection id="selectedSection">
            <SectionHeaderRow>
              <SectionTitle>
                <SectionIndicator $selected />
                {t('dashboardChatbot.selectedFiles', 'Selected Files')} <SectionCount>{selectedDocs.length}</SectionCount>
              </SectionTitle>
              {selectedDocs.length > 0 && <SectionStatus>CODiii is searching these</SectionStatus>}
            </SectionHeaderRow>

            {selectedDocs.length === 0 ? (
              <div style={{ fontSize: 12, color: '#888' }}>{t('dashboardChatbot.noFilesSelected', 'No files selected')}</div>
            ) : (
              selectedDocs
                .filter(d => d.title.toLowerCase().includes(sidePanelSearch.toLowerCase()))
                .filter(d => activeFilters.length ? docType(d) === activeFilters[0] : true)
                .map(d => {
                  const t = docType(d);
                  return (
                    <DocItem key={d.id} $selected onClick={() => handleDocumentToggle(d)}>
                      <DocIcon $type={t}>{(t === 'architectural' && 'ARCH') || (t === 'structural' && 'STR') || (t === 'mechanical' && 'MECH') || (t === 'contract' && 'DOC') || (t === 'client' && 'CLNT') || 'DOC'}</DocIcon>
                      <DocInfo>
                        <div className="name">{d.title}</div>
                        <div className="meta">
                          <Tag $type={t}>{t}</Tag>
                          {d.sizeMB && <Size>{d.sizeMB} MB</Size>}
                        </div>
                      </DocInfo>
                      <DocStatus>
                        <TickBox $checked>✓</TickBox>
                      </DocStatus>
                    </DocItem>
                  );
                })
            )}
          </DocSection>

          {/* Available Files */}
          <DocSection id="availableSection">
            <SectionHeaderRow>
              <SectionTitle>
                <SectionIndicator />
                {t('dashboardChatbot.availableFiles', 'Available Files')}
              </SectionTitle>
            </SectionHeaderRow>

            {filteredDocuments
              .filter(doc => !selectedDocs.some(sel => sel.id === doc.id))
              .filter(doc => doc.title.toLowerCase().includes(sidePanelSearch.toLowerCase()))
              .filter(doc => activeFilters.length ? docType(doc) === activeFilters[0] : true)
              .map(doc => {
                const t = docType(doc);
                return (
                  <DocItem key={doc.id} onClick={() => handleDocumentToggle(doc)}>
                    <DocIcon $type={t}>{(t === 'architectural' && 'ARCH') || (t === 'structural' && 'STR') || (t === 'mechanical' && 'MECH') || (t === 'contract' && 'DOC') || (t === 'client' && 'CLNT') || 'DOC'}</DocIcon>
                    <DocInfo>
                      <div className="name">{doc.title}</div>
                      <div className="meta">
                        <Tag $type={t}>{t}</Tag>
                        {doc.sizeMB && <Size>{doc.sizeMB} MB</Size>}
                      </div>
                    </DocInfo>
                    <DocStatus>
                      <TickBox $checked={false}>✓</TickBox>
                    </DocStatus>
                  </DocItem>
                );
              })}
          </DocSection>
          </>
          )}
        </DocumentsScroll>

        
      </DocumentsPanel>
      </SideInner>

            <SideRail
              $collapsed={rightCollapsed}
              $railWidth={50}
              type="button"
              onClick={() => setRightCollapsed(false)}
              aria-label="Expand documents panel"
              aria-expanded={!rightCollapsed}
            >
              <RailIcon aria-hidden="true">
                <FiFileText size={20} />
              </RailIcon>
            </SideRail>
  </SideShell>
)}

      </ChatContainer>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChosen}
      />


      {showDocumentDialog && projectId && (
        <DialogOverlay>
          <DocumentSelectionDialog>
            <DialogHeader>
              <h2>{t('dashboardChatbot.analyzeDocument', 'Analyze Document')}</h2>
              <CloseButton onClick={() => setShowDocumentDialog(false)}>×</CloseButton>
            </DialogHeader>
            
            <SearchBar>
              <SearchInput
                type="text"
                placeholder={t('dashboardChatbot.searchDocumentsByTitle', 'Search documents by title, URL, or requirement...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBar>

            <FilterChips>
              {documentCategories.map(category => (
                <FilterChip
                  key={category.id}
                  $isActive={activeFilters.includes(category.id)}
                  onClick={() => handleFilterToggle(category.id)}
                >
                  {category.label}
                </FilterChip>
              ))}
            </FilterChips>

            <DocumentList>
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map(doc => (             
                  <DocumentItem
                key={doc.id}
                $isSelected={selectedDocs.some(d => d.id === doc.id)}
                onClick={() => handleDocumentToggle(doc)}
              >
                <CustomCheckbox
                  type="checkbox"
                  checked={selectedDocs.some(d => d.id === doc.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleDocumentToggle(doc);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />


                <svg
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                    style={{ flexShrink: 0, color: '#3b82f6' }}
                  >
                    <path d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/>
                  </svg>

                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#2d2d2d' }}>{doc.title}</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{(doc.sizeMB || '2.1')} MB</div>
                </div>
              </DocumentItem>

                ))
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  {t('dashboardChatbot.noDocumentsFound', 'No documents found')}
                </div>
              )}
            </DocumentList>

            <DialogActions>
              <DialogButton 
                className="secondary" 
                onClick={() => setShowDocumentDialog(false)}
              >
                {t('dashboardChatbot.cancel', 'Cancel')}
              </DialogButton>
              <DialogButton 
                className="primary"
                onClick={() => {
                  if (selectedDocs.length > 0) {
                    handleDocumentSelect(selectedDocs);
                  }
                }}
                disabled={selectedDocs.length === 0}
              >
                {t('dashboardChatbot.analyzeDocument', 'Analyze Document')}
              </DialogButton>
            </DialogActions>
          </DocumentSelectionDialog>
        </DialogOverlay>
      )}
      <UploadToast
    open={uploadToast.open}
    text={uploadToast.text}
    onClose={() => setUploadToast({ open: false, text: '' })}
  />
  {pdfViewer.open && (
    <PdfViewerModal
      pdfUrl={pdfViewer.url}
      initialPage={pdfViewer.initialPage}
      title={pdfViewer.title}
      highlightText={pdfViewer.highlightText}
      onClose={() =>
        setPdfViewer({ open: false, url: null, initialPage: 1, title: '', highlightText: null })
      }
    />
  )}


    </ChatbotContainer>
    
  );
};


export default DashboardChatbot;